import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, ShieldAlert, ShieldCheck, Users as UsersIcon, Save, Building2, Briefcase, X, Check, Wallet, Settings, LogOut, FileClock, FolderKanban, RotateCcw, Filter, KeyRound, UserX, UserCheck, Copy, History, LayoutDashboard, Handshake, HardDrive, AlertOctagon, MessageSquare, Cog, Bell, Shield, Package, TrendingUp, Search, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import api, { formatDetail } from "@/lib/api";
import { DIVISION_COLOR, DIVISION_ORDER } from "@/lib/constants";
import AuditLogViewer from "@/components/AuditLogViewer";
import AuditLogButton from "@/components/AuditLogButton";
import SortableHeader from "@/components/SortableHeader";
import StickyContextBar from "@/components/StickyContextBar";
import SyncStickyTableHead from "@/components/table/SyncStickyTableHead";
import PettyCashSettingsDialog from "@/components/PettyCashSettingsDialog";
import UserFormDialog from "@/components/user-management/UserFormDialog";
import LegacyUsersBanner from "@/components/user-management/LegacyUsersBanner";
import AdminActionsCell from "@/components/user-management/AdminActionsCell";
import ProjectAccessView from "@/components/user-management/ProjectAccessView";
import TempPasswordDialog from "@/components/user-management/TempPasswordDialog";
import SessionHistoryTab from "@/components/user-management/SessionHistoryTab";
import WOSLASettingsPanel from "@/components/user-management/WOSLASettingsPanel";
import TriStatePermCell from "@/components/user-management/TriStatePermCell";
import {
  AccessMatrixHeatmap,
  AccessPolicyPresets,
  LegacyOverridesPanel,
  FeatureFilterAndCopy,
} from "@/components/user-management/AccessEnhancements";

const LEVELS = [1, 2, 3, 4, 5];
const ALL_DIVISIONS = ["BOD", "FAT", "Procurement Asset Logistic", "Project", "HRGA"];

const emptyForm = { name: "", email: "", password: "", division: "", sub_division: "", level: 3, position: "", preset_id: "" };

const DEFAULT_SORT = { key: "default", dir: "asc" };

export default function UserManagementPage() {
  const { user } = useAuth();
  const { canAccess, canPerm, reload: reloadAccess } = useAccess();
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [subDivisions, setSubDivisions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [revealAll, setRevealAll] = useState(false); // 149-ar: legacy state (unused, kept to avoid rewrite chain)
  const [revealOne, setRevealOne] = useState({}); // 149-ar: legacy state (unused)
  const [pcSettings, setPcSettings] = useState({ open: false, user: null });
  const [dialog, setDialog] = useState({ open: false, mode: "create", data: emptyForm });
  const [tempPwDlg, setTempPwDlg] = useState({ open: false, temp: "", user: null });  // 149-ar
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [tab, setTab] = useState("users");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  }, []);
  const loadRefs = useCallback(async () => {
    try {
      const [divs, subs, poss] = await Promise.all([api.get("/divisions"), api.get("/sub-divisions"), api.get("/positions")]);
      setDivisions(divs.data);
      setSubDivisions(subs.data);
      setPositions(poss.data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); loadRefs(); }, [load, loadRefs]);

  // Toggle per-user petty cash access override.
  // States cycle: matrix (no override) → force-grant → force-revoke → matrix.
  const togglePettyCashAccess = async (u) => {
    const cur = (u.access_overrides || {}).petty_cash;
    // Cycle: undefined → true (grant) → false (revoke) → undefined (matrix)
    const next = cur === undefined ? true : cur === true ? false : null;
    try {
      await api.patch(`/admin/users/${u.id}/access-override`, { feature: "petty_cash", allow: next });
      const label = next === true ? "diberi akses" : next === false ? "dicabut akses" : "dikembalikan ke default (matrix)";
      toast.success(`Akses Petty Cash ${u.name}: ${label}`);
      await load();
      await reloadAccess();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const sortedUsers = useMemo(() => {
    const arr = [...users];
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    if (key === "default") {
      arr.sort((a, b) => {
        const dv = (DIVISION_ORDER[a.division] ?? 99) - (DIVISION_ORDER[b.division] ?? 99);
        if (dv !== 0) return dv;
        if (a.level !== b.level) return a.level - b.level;
        return a.name.localeCompare(b.name);
      });
    } else if (key === "level") {
      arr.sort((a, b) => (a.level - b.level) * mul || a.name.localeCompare(b.name));
    } else if (key === "division") {
      arr.sort((a, b) => {
        const dv = ((DIVISION_ORDER[a.division] ?? 99) - (DIVISION_ORDER[b.division] ?? 99)) * mul;
        if (dv !== 0) return dv;
        if (a.level !== b.level) return a.level - b.level;
        return a.name.localeCompare(b.name);
      });
    } else if (key === "email") {
      arr.sort((a, b) => a.email.localeCompare(b.email) * mul);
    }
    return arr;
  }, [users, sort]);

  const canViewUsers = canPerm(user, "user_management", "view");
  const canCreateUser = canPerm(user, "user_management", "create");
  const canUpdateUser = canPerm(user, "user_management", "update");
  const canDeleteUser = canPerm(user, "user_management", "delete");
  // 149-ct (PROMPT 16-SEC-R1): role tag derived dari session user aktif (bukan hardcoded).
  const isBODLv1 = user?.division === "BOD" && user?.level === 1;
  if (!canViewUsers) {
    return (
      <div className="p-8">
        <div className="max-w-md bg-rose-50 border border-rose-200 p-6 text-sm text-rose-800 flex items-start gap-3">
          <ShieldAlert size={20} className="shrink-0" />
          <div>
            <div className="font-semibold mb-1">Akses ditolak</div>
            Anda belum memiliki akses ke User Management. Hubungi BOD Lv1 untuk mendapatkan akses (perm <b>Lihat</b> pada fitur <b>User Management</b>).
          </div>
        </div>
      </div>
    );
  }

  const openCreate = () => setDialog({ open: true, mode: "create", data: { ...emptyForm } });
  const openEdit = (u) => setDialog({ open: true, mode: "edit", data: { id: u.id, name: u.name, email: u.email, password: "", division: u.division, sub_division: u.sub_division, level: u.level, position: u.position || "" } });

  const submit = async (e) => {
    e.preventDefault();
    const d = dialog.data;
    setBusy(true);
    try {
      if (dialog.mode === "create") {
        await api.post("/admin/users", d);
        toast.success("User created");
      } else {
        const payload = { name: d.name, division: d.division, sub_division: d.sub_division, level: d.level, position: d.position };
        if (d.password) payload.password = d.password;
        await api.patch(`/admin/users/${d.id}`, payload);
        toast.success("User updated");
      }
      setDialog({ ...dialog, open: false });
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete account for ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const forceLogout = async (u) => {
    if (!window.confirm(`Paksa keluar (force logout) untuk ${u.name}?\n\nSemua sesi aktif akan langsung invalid. Pengguna harus login ulang di device manapun.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/force-logout`);
      if (data.was_logged_in) toast.success(`${u.name} telah dipaksa keluar.`);
      else toast.info(`${u.name} sudah tidak sedang login.`);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          {/* 149-ct (PROMPT 16-SEC-R1): role label sekarang derived dari session
              user aktif — sebelumnya hardcoded "Level 1 · Super Admin" sehingga
              HRGA LV2 (yg lolos gate lewat matrix wide-open) melihat title yg
              salah. `isBODLv1` benar-benar tandai super-admin; user lain lihat
              divisi + level asli. */}
          <p className="overline text-slate-500" data-testid="um-role-tag">
            {isBODLv1
              ? "Level 1 · Super Admin"
              : `${user?.division || "?"} Lv${user?.level ?? "?"}`}
          </p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">User Management</h1>
          <p className="text-sm text-slate-500 mt-1">Tambah, ubah, dan kelola akun personel. Password disimpan sebagai <b>bcrypt hash one-way</b> — tidak dapat dilihat oleh siapapun. Reset via tombol <b>Reset</b> per baris.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {sort.key !== "default" && (
            <button data-testid="btn-reset-sort" onClick={() => setSort(DEFAULT_SORT)} className="border border-slate-300 h-9 px-3 uppercase tracking-wider font-semibold text-xs flex items-center gap-1.5 hover:bg-slate-50 text-slate-600">
              Reset Sort
            </button>
          )}
          {/* 149-ar: reveal-password toggle DIHAPUS. Password sekarang one-way bcrypt hash;
              tidak bisa dilihat oleh siapapun, termasuk BOD. Reset via aksi per-baris. */}
          {canCreateUser && (
            <Button data-testid="btn-add-user" onClick={openCreate} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold h-9">
              <Plus size={14} className="mr-1" /> Add User
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="admin-tabs">
        <button
          type="button"
          data-testid="tab-users"
          onClick={() => setTab("users")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "users" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><UsersIcon size={13} /> Users</button>
        <button
          type="button"
          data-testid="tab-divisions"
          onClick={() => setTab("divisions")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "divisions" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><Building2 size={13} /> Divisions</button>
        <button
          type="button"
          data-testid="tab-positions"
          onClick={() => setTab("positions")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "positions" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><Briefcase size={13} /> Positions</button>
        <button
          type="button"
          data-testid="tab-access"
          onClick={() => setTab("access")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "access" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><ShieldCheck size={13} /> Access Control</button>
        <button
          type="button"
          data-testid="tab-client-access"
          onClick={() => setTab("client_access")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "client_access" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><Wallet size={13} /> Akses Client &amp; Project</button>
        <button
          type="button"
          data-testid="tab-access-audit"
          onClick={() => setTab("access_audit")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "access_audit" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><FileClock size={13} /> Audit Log Akses</button>
        <button
          type="button"
          data-testid="tab-audit-log-all"
          onClick={() => setTab("audit_log_all")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "audit_log_all" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><FileClock size={13} /> Audit Log Global</button>
        {(isBODLv1 || canPerm(user, "session_history", "view") || canPerm(user, "personnel_manage", "view")) && (
          <button
            type="button"
            data-testid="tab-session-history"
            onClick={() => setTab("session_history")}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "session_history" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
          ><History size={13} /> Riwayat Login &amp; Sesi</button>
        )}
      </div>

      {/* 149-bn: Sticky context bar — memberi tahu user tab aktif saat scroll */}
      <StickyContextBar
        segments={[
          "User Management",
          tab === "users" ? `Users (${sortedUsers.length})` :
          tab === "divisions" ? "Divisions & Sub-Divisions" :
          tab === "positions" ? "Positions" :
          tab === "access" ? "Access Control Matrix" :
          tab === "client_access" ? "Akses Client & Project" :
          tab === "access_audit" ? "Audit Log Akses" :
          tab === "audit_log_all" ? "Audit Log Global (semua kategori)" :
          tab === "session_history" ? "Riwayat Login & Sesi" : "",
        ]}
        extra={<span className="text-slate-400 normal-case" data-testid="um-role-context">{
          isBODLv1 ? "BOD Lv1 · Super Admin" : `${user?.division || "?"} Lv${user?.level ?? "?"}`
        }</span>}
      />

      {tab === "users" && (
      <section>
        <LegacyUsersBanner onDone={load} />
        <SyncStickyTableHead offset="context" testid="users-table-wrapper" className="bg-white border border-slate-200">
        <table className="w-full text-sm">
          {/* R74 fix: class `ens-sticky-thead` DIHAPUS — tabel ini sudah dibungkus
              SyncStickyTableHead (JS clone fixed). Dua mekanisme sticky bersamaan
              menyebabkan header kolom ganda bertumpuk menutupi baris data. */}
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-2.5 overline text-slate-600 whitespace-nowrap">Name</th>
              <SortableHeader label="Email" k="email" sort={sort} setSort={setSort} />
              <SortableHeader label="Division" k="division" sort={sort} setSort={setSort} />
              <th className="px-4 py-2.5 overline text-slate-600 whitespace-nowrap">Sub Division</th>
              <SortableHeader label="Level" k="level" sort={sort} setSort={setSort} />
              <th className="px-4 py-2.5 overline text-slate-600 whitespace-nowrap">Position</th>
              {/* 149-ar: kolom "Password" DIHAPUS. Diganti kolom "Security Status" */}
              <th className="px-4 py-2.5 overline text-slate-600 whitespace-nowrap">Security</th>
              <th className="px-4 py-2.5 overline text-slate-600 whitespace-nowrap" title="Akses Weekly Petty Cash — override per-user (klik untuk cycle grant / revoke / default matrix)">Petty Cash</th>
              <th className="px-4 py-2.5 overline text-slate-600 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-users-table">
            {sortedUsers.map((u) => {
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-[#0B1D3A]">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[u.division] || "bg-slate-600 text-white"}`}>{u.division}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap" data-testid={`sub-div-${u.id}`}>{u.sub_division || <span className="text-slate-300 italic">—</span>}</td>
                  <td className="px-4 py-3"><span className="px-1.5 py-0.5 text-[10px] bg-slate-100 font-semibold uppercase tracking-wider">Lv{u.level}</span></td>
                  <td className="px-4 py-3 text-xs text-[#FF5A1F] font-medium whitespace-nowrap" data-testid={`pos-${u.id}`}>{u.position || <span className="text-slate-300 italic font-normal">—</span>}</td>
                  <td className="px-4 py-3" data-testid={`sec-status-${u.id}`}>
                    {/* 149-ar: Security badge — Aktif/Nonaktif + Must-change indicator */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {u.is_active === false ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-rose-400 bg-rose-50 text-rose-700"><UserX size={10} /> Nonaktif</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-emerald-400 bg-emerald-50 text-emerald-700"><UserCheck size={10} /> Aktif</span>
                      )}
                      {u.must_change_password && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-amber-400 bg-amber-50 text-amber-700" title="User harus ganti password saat login berikutnya">
                          <KeyRound size={10} /> Wajib Ganti
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3" data-testid={`pc-access-cell-${u.id}`}>
                    {(() => {
                      const ov = (u.access_overrides || {}).petty_cash;
                      const isBODLv1 = u.division === "BOD" && u.level === 1;
                      const stateLabel = isBODLv1 ? "Super Admin" : ov === true ? "Diberi Akses" : ov === false ? "Dicabut" : "Matrix (default)";
                      const stateClass = isBODLv1
                        ? "border-slate-300 bg-slate-100 text-slate-500"
                        : ov === true ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                        : ov === false ? "border-rose-400 bg-rose-50 text-rose-700"
                        : "border-slate-300 bg-slate-50 text-slate-600";
                      const pcMax = Number(u.petty_cash_max) || 0;
                      const hasLimit = pcMax > 0;
                      const compactMax = hasLimit
                        ? (pcMax >= 1_000_000
                          ? `${(pcMax / 1_000_000).toFixed(pcMax % 1_000_000 === 0 ? 0 : 1)}JT`
                          : `${Math.round(pcMax / 1000)}RB`)
                        : "";
                      return (
                        <div className="flex items-center gap-1.5">
                          <button
                            data-testid={`btn-toggle-pc-access-${u.id}`}
                            disabled={isBODLv1}
                            onClick={() => togglePettyCashAccess(u)}
                            title={isBODLv1 ? "BOD Lv1 selalu punya akses" : "Klik untuk cycle: Matrix → Grant → Revoke → Matrix"}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-semibold uppercase tracking-wider transition-colors ${stateClass} ${isBODLv1 ? "cursor-not-allowed opacity-60" : "hover:border-[#FF5A1F]"}`}
                          >
                            <Wallet size={11} /> {stateLabel}
                          </button>
                          {hasLimit && (
                            <span
                              data-testid={`pc-limit-badge-${u.id}`}
                              title={`Batas maksimal saldo: Rp ${pcMax.toLocaleString("id-ID")}`}
                              className="px-1 py-0.5 border border-amber-300 bg-amber-50 text-amber-700 text-[9px] tabular-nums font-semibold"
                            >
                              MAX {compactMax}
                            </span>
                          )}
                          <button
                            data-testid={`btn-pc-settings-${u.id}`}
                            disabled={isBODLv1}
                            onClick={() => setPcSettings({ open: true, user: u })}
                            title={isBODLv1 ? "BOD Lv1: tidak perlu pengaturan" : "Pengaturan lengkap: Akses + Maksimal Saldo"}
                            className={`p-1 border border-slate-200 text-slate-500 hover:text-[#FF5A1F] hover:border-[#FF5A1F] transition-colors ${isBODLv1 ? "cursor-not-allowed opacity-40" : ""}`}
                          >
                            <Settings size={11} />
                          </button>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap flex gap-2 items-center">
                    <Button data-testid={`btn-edit-${u.id}`} onClick={() => openEdit(u)} size="sm" variant="outline" className="h-7 rounded-none text-xs"><Pencil size={12} className="mr-1" /> Edit</Button>
                    {/* 149-ar: Admin security actions (BOD Lv1 only): reset password, disable/enable, force-logout */}
                    <AdminActionsCell
                      user={u}
                      currentUser={user}
                      canUpdateUser={canUpdateUser}
                      onTempPassword={({ user: tu, temp }) => setTempPwDlg({ open: true, temp, user: tu })}
                      onReload={load}
                    />
                    <Button data-testid={`btn-delete-${u.id}`} onClick={() => del(u)} size="sm" variant="outline" disabled={u.id === user.id} className="h-7 rounded-none text-xs text-rose-600 border-rose-300 hover:bg-rose-50 disabled:opacity-40"><Trash2 size={12} className="mr-1" /> Delete</Button>
                    {/* 2026-09-25: Audit trail per user (mengagregasi kategori access_control, profile, salary, session, dll dengan target_user_id yang cocok) */}
                    <AuditLogButton feature_key="user_management" target_id={u.id} title={u.name} size="sm" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </SyncStickyTableHead>
      </section>
      )}

      {tab === "divisions" && (
        <DivisionsPanel divisions={divisions} subDivisions={subDivisions} onReload={loadRefs} />
      )}

      {tab === "positions" && (
        <PositionsPanel positions={positions} divisions={divisions} subDivisions={subDivisions} onReload={loadRefs} />
      )}

      {tab === "access" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-slate-500 italic">Setiap perubahan matriks akses tercatat di audit log.</p>
            <AuditLogButton feature_key="access_control" title="Access Control Matrix" />
          </div>
          <AccessControlPanel onSaved={reloadAccess} />
          {/* R3.2C: WO SLA settings dipindah keluar dari region Access Control Matrix
              supaya tidak nampak sebagai bagian dari matrix. Collapsed by default. */}
          <details className="bg-white border border-slate-200" data-testid="wo-sla-settings-wrapper">
            <summary className="cursor-pointer px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider font-semibold text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] flex items-center gap-2">
              <span aria-hidden="true">▸</span>
              <span>Pengaturan WO SLA & Cron</span>
              <span className="text-[9px] text-slate-500 italic normal-case font-normal">— pengaturan terpisah, bukan bagian Access Control Matrix</span>
            </summary>
            <div className="p-3">
              <WOSLASettingsPanel />
            </div>
          </details>
        </div>
      )}

      {tab === "client_access" && (
        <ClientProjectAccessPanel divisions={divisions} />
      )}

      {tab === "access_audit" && (
        <ClientAccessAuditLogPanel divisions={divisions} />
      )}

      {tab === "audit_log_all" && (
        <AuditLogViewer />
      )}

      {tab === "session_history" && (isBODLv1 || canPerm(user, "session_history", "view") || canPerm(user, "personnel_manage", "view")) && (
        <SessionHistoryTab />
      )}

      <UserFormDialog dialog={dialog} setDialog={setDialog} onSubmit={submit} busy={busy} divisions={divisions} subDivisions={subDivisions} positions={positions} />
      <PettyCashSettingsDialog
        open={pcSettings.open}
        onOpenChange={(o) => setPcSettings((s) => ({ ...s, open: o }))}
        user={pcSettings.user}
        onSaved={() => { load(); reloadAccess(); }}
      />
      {/* 149-ar: One-time reveal password sementara setelah admin reset */}
      <TempPasswordDialog
        open={tempPwDlg.open}
        onOpenChange={(o) => setTempPwDlg((s) => ({ ...s, open: o }))}
        user={tempPwDlg.user}
        tempPassword={tempPwDlg.temp}
      />
    </div>
  );
}

function DivisionsPanel({ divisions, subDivisions, onReload }) {
  const [newDiv, setNewDiv] = useState("");
  const [newSubBy, setNewSubBy] = useState({}); // { [divisionName]: "sub name" }
  const [busy, setBusy] = useState(false);
  const [editingDiv, setEditingDiv] = useState({}); // { [id]: draftName }
  const [editingSub, setEditingSub] = useState({}); // { [subId]: draftName }

  const addDivision = async (e) => {
    e.preventDefault();
    const name = newDiv.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/divisions", { name });
      toast.success(`Divisi ${name} ditambahkan`);
      setNewDiv("");
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const saveDivName = async (d) => {
    const name = (editingDiv[d.id] || "").trim();
    if (!name || name === d.name) { setEditingDiv((s) => { const c = { ...s }; delete c[d.id]; return c; }); return; }
    try {
      await api.patch(`/divisions/${d.id}`, { name });
      toast.success(`Divisi ${d.name} → ${name}`);
      setEditingDiv((s) => { const c = { ...s }; delete c[d.id]; return c; });
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const saveSubName = async (sub) => {
    const name = (editingSub[sub.id] || "").trim();
    if (!name || name === sub.name) { setEditingSub((s) => { const c = { ...s }; delete c[sub.id]; return c; }); return; }
    try {
      await api.patch(`/sub-divisions/${sub.id}`, { name });
      toast.success(`Sub-divisi ${sub.name} → ${name}`);
      setEditingSub((s) => { const c = { ...s }; delete c[sub.id]; return c; });
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const delDivision = async (d) => {
    const msg = `Hapus divisi "${d.name}"?\n\n⚠️ Semua staff pada divisi ini akan kehilangan divisi & sub-divisi mereka (perlu di-assign ulang). Semua sub-divisi & pengaturan Access Control divisi ini juga akan dihapus.\n\nLanjutkan?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/divisions/${d.id}`);
      const parts = [];
      if (data?.users_cleared) parts.push(`${data.users_cleared} user di-clear`);
      if (data?.sub_divisions_deleted) parts.push(`${data.sub_divisions_deleted} sub-divisi dihapus`);
      if (data?.access_control_features_touched) parts.push(`${data.access_control_features_touched} matrix di-cleanup`);
      toast.success(`Divisi ${d.name} dihapus${parts.length ? " — " + parts.join(", ") : ""}`);
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const addSub = async (divName) => {
    const name = (newSubBy[divName] || "").trim();
    if (!name) return;
    try {
      await api.post("/sub-divisions", { division: divName, name });
      toast.success(`Sub-divisi ${name} ditambahkan`);
      setNewSubBy((s) => ({ ...s, [divName]: "" }));
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const delSub = async (sub) => {
    if (!window.confirm(`Hapus sub-divisi "${sub.name}"?\n\nSemua user yang di-assign ke sub-divisi ini akan di-unassign otomatis (field sub_division dikosongkan).`)) return;
    try {
      const { data } = await api.delete(`/sub-divisions/${sub.id}`);
      const n = data?.affected_users || 0;
      toast.success(n > 0 ? `Sub-divisi ${sub.name} dihapus — ${n} user di-unassign` : `Sub-divisi ${sub.name} dihapus`);
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  // Group sub-divisions by division for display; ensure every division shows even if empty.
  const grouped = divisions.map((d) => ({
    ...d,
    subs: subDivisions.filter((s) => s.division === d.name),
  }));

  return (
    <section className="space-y-4" data-testid="divisions-panel">
      <div className="bg-white border border-slate-200 p-4">
        <div className="text-xs font-semibold text-[#0B1D3A] mb-2 flex items-center gap-1.5"><Plus size={13} /> Tambah Divisi Baru</div>
        <form onSubmit={addDivision} className="flex items-center gap-2">
          <Input
            data-testid="input-new-division"
            value={newDiv}
            onChange={(e) => setNewDiv(e.target.value)}
            placeholder="Nama divisi (contoh: IT, Marketing)"
            className="rounded-none h-9 text-sm max-w-xs"
          />
          <Button
            type="submit"
            data-testid="btn-add-division"
            disabled={busy || !newDiv.trim()}
            className="rounded-none h-9 bg-[#0B1D3A] hover:bg-[#0B1D3A]/90 text-xs uppercase tracking-wider"
          ><Plus size={13} className="mr-1" /> Tambah Divisi</Button>
          <div className="text-[10px] text-slate-500 ml-2">
            <ShieldAlert size={10} className="inline mr-0.5 text-amber-500" /> Semua divisi bisa dihapus. Staff pada divisi yang dihapus akan menjadi tanpa divisi & sub-divisi (perlu di-assign ulang).
          </div>
        </form>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {grouped.map((d) => (
          <section key={d.id} className="bg-white border border-slate-200" data-testid={`division-card-${d.name}`}>
            <header className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {editingDiv[d.id] !== undefined ? (
                  <>
                    <Input
                      data-testid={`input-edit-division-${d.id}`}
                      value={editingDiv[d.id]}
                      onChange={(e) => setEditingDiv((s) => ({ ...s, [d.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") saveDivName(d); if (e.key === "Escape") setEditingDiv((s) => { const c = { ...s }; delete c[d.id]; return c; }); }}
                      className="rounded-none h-7 text-xs max-w-[160px]"
                      autoFocus
                    />
                    <button type="button" data-testid={`btn-save-division-${d.id}`} onClick={() => saveDivName(d)} className="text-emerald-600 hover:text-emerald-800" title="Simpan"><Check size={13} /></button>
                    <button type="button" onClick={() => setEditingDiv((s) => { const c = { ...s }; delete c[d.id]; return c; })} className="text-slate-400 hover:text-slate-700" title="Batal"><X size={13} /></button>
                  </>
                ) : (
                  <>
                    <span className={`px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[d.name] || "bg-slate-600 text-white"}`}>{d.name}</span>
                    {d.is_core ? (
                      <span className="text-[9px] uppercase tracking-wider text-slate-400">Core</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-wider text-emerald-600">Custom</span>
                    )}
                    <button
                      type="button"
                      data-testid={`btn-edit-division-${d.id}`}
                      onClick={() => {
                        if (d.is_core && !window.confirm(`"${d.name}" adalah divisi inti. Rename dapat memengaruhi RBAC (Petty Cash approval, FAT reports, dll). Lanjutkan?`)) return;
                        setEditingDiv((s) => ({ ...s, [d.id]: d.name }));
                      }}
                      className="text-slate-400 hover:text-[#FF5A1F]"
                      title={d.is_core ? "Rename divisi inti (hati-hati)" : "Rename divisi"}
                    ><Pencil size={11} /></button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] tabular-nums text-slate-500">{d.subs.length} sub-divisi</span>
                <button
                  type="button"
                  data-testid={`btn-delete-division-${d.name}`}
                  onClick={() => delDivision(d)}
                  className="text-rose-500 hover:text-rose-700"
                  title={d.is_core ? "Hapus divisi inti (staff terkait akan kehilangan divisi)" : "Hapus divisi"}
                ><Trash2 size={13} /></button>
              </div>
            </header>
            <div className="p-3 space-y-2">
              <ul className="space-y-1">
                {d.subs.length === 0 && (
                  <li className="text-[11px] italic text-slate-400 py-2 text-center">Belum ada sub-divisi</li>
                )}
                {d.subs.map((s) => (
                  <li key={s.id} className="flex items-center justify-between py-1 px-2 bg-slate-50 border border-slate-100 text-xs" data-testid={`sub-item-${s.id}`}>
                    {editingSub[s.id] !== undefined ? (
                      <div className="flex items-center gap-1 flex-1">
                        <Input
                          data-testid={`input-edit-sub-${s.id}`}
                          value={editingSub[s.id]}
                          onChange={(e) => setEditingSub((st) => ({ ...st, [s.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") saveSubName(s); if (e.key === "Escape") setEditingSub((st) => { const c = { ...st }; delete c[s.id]; return c; }); }}
                          className="rounded-none h-6 text-xs flex-1"
                          autoFocus
                        />
                        <button type="button" data-testid={`btn-save-sub-${s.id}`} onClick={() => saveSubName(s)} className="text-emerald-600 hover:text-emerald-800" title="Simpan"><Check size={12} /></button>
                        <button type="button" onClick={() => setEditingSub((st) => { const c = { ...st }; delete c[s.id]; return c; })} className="text-slate-400 hover:text-slate-700" title="Batal"><X size={12} /></button>
                      </div>
                    ) : (
                      <>
                        <span className="text-slate-700 flex-1">{s.name}</span>
                        <button
                          type="button"
                          data-testid={`btn-edit-sub-${s.id}`}
                          onClick={() => setEditingSub((st) => ({ ...st, [s.id]: s.name }))}
                          className="text-slate-400 hover:text-[#FF5A1F] mr-1"
                          title="Rename sub-divisi"
                        ><Pencil size={11} /></button>
                        <button
                          type="button"
                          data-testid={`btn-delete-sub-${s.id}`}
                          onClick={() => delSub(s)}
                          className="text-slate-400 hover:text-rose-600"
                          title="Hapus sub-divisi"
                        ><X size={13} /></button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <form
                onSubmit={(e) => { e.preventDefault(); addSub(d.name); }}
                className="flex items-center gap-1.5 pt-1 border-t border-slate-100"
              >
                <Input
                  data-testid={`input-new-sub-${d.name}`}
                  value={newSubBy[d.name] || ""}
                  onChange={(e) => setNewSubBy((s) => ({ ...s, [d.name]: e.target.value }))}
                  placeholder={`Nama sub-divisi ${d.name}…`}
                  className="rounded-none h-8 text-xs flex-1"
                />
                <Button
                  type="submit"
                  data-testid={`btn-add-sub-${d.name}`}
                  disabled={!(newSubBy[d.name] || "").trim()}
                  size="sm"
                  className="rounded-none h-8 bg-[#FF5A1F] hover:bg-[#e04a13] text-[10px] uppercase tracking-wider"
                ><Plus size={11} className="mr-0.5" /> Tambah</Button>
              </form>
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

function PositionsPanel({ positions, divisions, subDivisions, onReload }) {
  const [dialog, setDialog] = useState(null); // { mode, data }
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });

  const sorted = useMemo(() => {
    const arr = [...positions];
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    if (key === "name") arr.sort((a, b) => (a.name || "").localeCompare(b.name || "") * mul);
    else if (key === "division") arr.sort((a, b) => ((a.division || "~").localeCompare(b.division || "~") * mul) || (a.name || "").localeCompare(b.name || ""));
    return arr;
  }, [positions, sort]);

  const openCreate = () => setDialog({ mode: "create", data: { name: "", division: "" } });
  const openEdit = (p) => setDialog({ mode: "edit", data: { id: p.id, name: p.name, division: p.division || "" } });
  const close = () => setDialog(null);

  const save = async () => {
    if (!dialog) return;
    const { mode, data } = dialog;
    const name = (data.name || "").trim();
    if (!name) { toast.error("Nama posisi wajib diisi"); return; }
    const payload = { name, division: data.division || null };
    setBusy(true);
    try {
      if (mode === "create") {
        await api.post("/positions", payload);
        toast.success(`Posisi ${name} ditambahkan`);
      } else {
        await api.patch(`/positions/${data.id}`, payload);
        toast.success(`Posisi ${name} diperbarui`);
      }
      close();
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Hapus posisi "${p.name}"?`)) return;
    try {
      await api.delete(`/positions/${p.id}`);
      toast.success(`Posisi ${p.name} dihapus`);
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  return (
    <section className="space-y-4" data-testid="positions-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-500">Kelola daftar posisi (jabatan). Field <span className="font-semibold">Division</span> dan <span className="font-semibold">Sub-Division</span> bersifat opsional — untuk mengelompokkan posisi ke unit tertentu.</div>
        <Button
          data-testid="btn-open-add-position"
          onClick={openCreate}
          className="rounded-none h-9 bg-[#FF5A1F] hover:bg-[#e04a13] text-xs uppercase tracking-wider font-semibold"
        ><Plus size={13} className="mr-1" /> Add Position</Button>
      </div>

      <SyncStickyTableHead offset="context" className="bg-white border border-slate-200">
        <table className="w-full text-sm" data-testid="positions-table">
          {/* R74 fix: `ens-sticky-thead` dihapus — stickiness ditangani
              SyncStickyTableHead; ganda = header bertumpuk. */}
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              <SortableHeader label="Position" k="name" sort={sort} setSort={setSort} />
              <SortableHeader label="Division" k="division" sort={sort} setSort={setSort} />
              <th className="px-4 py-2.5 overline text-slate-600 whitespace-nowrap text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={3} className="text-[11px] italic text-slate-400 py-6 text-center">Belum ada posisi. Klik &quot;Add Position&quot; untuk menambah.</td></tr>
            )}
            {sorted.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`position-row-${p.id}`}>
                <td className="px-4 py-3 font-medium text-[#0B1D3A]">{p.name}</td>
                <td className="px-4 py-3">
                  {p.division ? (
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[p.division] || "bg-slate-600 text-white"}`}>{p.division}</span>
                  ) : <span className="text-slate-300 italic text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    data-testid={`btn-edit-position-${p.id}`}
                    onClick={() => openEdit(p)}
                    className="text-slate-400 hover:text-[#FF5A1F] mr-3"
                    title="Edit"
                  ><Pencil size={13} /></button>
                  <button
                    type="button"
                    data-testid={`btn-delete-position-${p.id}`}
                    onClick={() => del(p)}
                    className="text-slate-400 hover:text-rose-600"
                    title="Hapus"
                  ><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SyncStickyTableHead>

      {dialog && (
        <PositionDialog
          dialog={dialog}
          setDialog={setDialog}
          onSave={save}
          onClose={close}
          divisions={divisions}
          subDivisions={subDivisions}
          busy={busy}
        />
      )}
    </section>
  );
}

function PositionDialog({ dialog, setDialog, onSave, onClose, divisions, subDivisions, busy }) {
  const d = dialog.data;
  const set = (patch) => setDialog({ ...dialog, data: { ...d, ...patch } });
  const changeDivision = (v) => {
    const newDiv = v === "__none__" ? "" : v;
    set({ division: newDiv });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="position-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{dialog.mode === "create" ? "Tambah Posisi" : "Edit Posisi"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSave(); }} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Nama Posisi</Label>
            <Input
              data-testid="pf-name"
              required
              value={d.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Contoh: Project Manager, Site Manager, Estimator"
              className="rounded-none"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Division <span className="text-slate-400 normal-case tracking-normal font-normal">(opsional)</span></Label>
            <Select value={d.division || "__none__"} onValueChange={changeDivision}>
              <SelectTrigger data-testid="pf-division" className="rounded-none"><SelectValue placeholder="Pilih…" /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="__none__">— Tanpa Divisi —</SelectItem>
                {divisions.map((x) => <SelectItem key={x.id} value={x.name}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={onClose}>Batal</Button>
            <Button type="submit" data-testid="pf-submit" disabled={busy || !d.name.trim()} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
              {busy ? "Menyimpan…" : (dialog.mode === "create" ? "Tambah" : "Simpan")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AccessControlPanel({ onSaved }) {
  const [rows, setRows] = useState([]);
  const [initialRows, setInitialRows] = useState([]);
  const [allDivisions, setAllDivisions] = useState([]);
  const [staffList, setStaffList] = useState([]); // [{id,name,email,division,sub_division,level}]
  // 16-AC-MATRIX-UI-R2: staff search filter + reset-override confirm dialog
  // 2026-10-06: state staffSearch dihapus — pencarian kini internal SearchableSelect.
  const [resetConfirm, setResetConfirm] = useState(false);
  const [activeDiv, setActiveDiv] = useState("");
  const [activeLevel, setActiveLevel] = useState(1);
  const [targetMode, setTargetMode] = useState("division"); // "division" | "staff"
  const [activeUserId, setActiveUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // 16-AC-MATRIX-UI-R2 FASE-2 (2026-08-26 149-cw-r49):
  // - accordion state per-module (default: all open when there are dirty/matches, else collapsed)
  // - filter chip: "all" | "inherited" | "allow" | "deny" | "changed"
  // - dependency assistance dialog (menampilkan permission yang perlu diaktifkan tambahan)
  // - per-perm reset button (staff mode, hanya muncul kalau ada override)
  const [collapsedGroups, setCollapsedGroups] = useState({}); // {moduleKey: true}
  const [filterChip, setFilterChip] = useState("all");
  const [depsDialog, setDepsDialog] = useState(null); // {feature, key, requires: [...], onConfirm}
  const [visDenyDialog, setVisDenyDialog] = useState(null); // { feature, onConfirm } konfirmasi Tolak akses via user_perms_deny.view

  // NOTE: `load` intentionally reads activeDiv/activeUserId to seed defaults only on FIRST
  // load. We disable deps ESLint below because re-firing on those state changes would refetch
  // the entire matrix on every user interaction. Fetch happens once on mount.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [acRes, divRes, usrRes] = await Promise.all([
        api.get("/access-controls"),
        api.get("/divisions"),
        api.get("/users"),
      ]);
      setRows(acRes.data || []);
      setInitialRows(JSON.parse(JSON.stringify(acRes.data || [])));
      const divs = (divRes.data || []).map((d) => d.name);
      divs.sort((a, b) => (DIVISION_ORDER[a] ?? 99) - (DIVISION_ORDER[b] ?? 99) || a.localeCompare(b));
      setAllDivisions(divs);
      setActiveDiv((cur) => (cur && divs.includes(cur) ? cur : (divs[0] || "")));
      const users = (usrRes.data || []).map((u) => ({
        id: u.id, name: u.name, division: u.division, sub_division: u.sub_division, level: u.level,
      }));
      users.sort((a, b) => (DIVISION_ORDER[a.division] ?? 99) - (DIVISION_ORDER[b.division] ?? 99)
        || (a.level || 0) - (b.level || 0) || (a.name || "").localeCompare(b.name || ""));
      setStaffList(users);
      setActiveUserId((cur) => (cur || (users[0]?.id ?? "")));
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const LV_LIST = [1, 2, 3, 4, 5];
  // 16-PETTY-CASH-APPROVAL-ACCESS-CONTROL-R1: extended perms untuk Weekly Petty Cash.
  // Kunci teknis stabil di backend, label Indonesia untuk UI.
  const PC_EXTENDED = [
    { key: "approve",          label: "Approve pengajuan" },
    { key: "reject",           label: "Reject pengajuan" },
    { key: "edit_own",         label: "Edit pengajuan sendiri" },
    { key: "edit_all",         label: "Edit pengajuan orang lain" },
    { key: "assign_approver",  label: "Assign approver (koordinasi)" },
    { key: "reopen",           label: "Reopen pengajuan approved" },
    { key: "restore",          label: "Restore dari Trash" },
    { key: "verify_actual",    label: "Verifikasi realisasi/aktual" },
    // 149-cw-r180: tahapan Check pasca-verifikasi (terpisah dari verifikasi).
    { key: "check",            label: "Check pasca-verifikasi" },
    { key: "confirm_transfer", label: "Konfirmasi transfer" },
    { key: "export",           label: "Export CSV/XLSX" },
    { key: "self_approve",     label: "Self-approve (bahaya — default OFF)" },
  ];

  const PERM_LABELS = [
    { key: "active", label: "Aktif", hint: "Master toggle — kalau OFF, permission lain otomatis diabaikan" },
    { key: "create", label: "Buat" },
    { key: "update", label: "Ubah" },
    { key: "delete", label: "Hapus / Close / Cancel", hint: "Juga me-gate tombol Close & Cancel di modul terkait (mis. tutup minggu petty cash, cancel pengajuan)" },
    { key: "view",   label: "Lihat" },
    // 149-cw-r170: perm granular `close` — TERPISAH dari delete, hanya berlaku
    // untuk fitur Projects (close/reopen WO & project). Fitur lain render "—".
    { key: "close",  label: "Close", hint: "Khusus Projects — close/reopen WO & project" },
  ];
  const SCOPE_OPTIONS = [
    { value: "own",             label: "Hanya milik sendiri" },
    { value: "division",        label: "Se-divisi" },
    { value: "division_lower",  label: "Se-divisi + level lebih bawah" },
    { value: "all",             label: "Semua" },
  ];

  // 16-AC-MATRIX-UI-R2 FASE-2 (2026-08-26): module grouping (frontend-only, tidak
  // mengubah default permission, matrix, atau schema backend). Setiap fitur di-map
  // ke module berdasarkan key. Fitur yang tidak masuk mapping akan jatuh ke "Lainnya".
  const MODULE_GROUPS = useMemo(() => ([
    // R3.2D.1: icons per mockup — distinct line icons, konsisten 14px stroke.
    // Weekly Petty Cash aksen hijau ada di PC panel header (bukan di module icon
    // karena Petty Cash adalah feature di dalam Financial & Petty Cash module).
    { key: "user_admin",   label: "User Administration", icon: UsersIcon,       featureKeys: ["user_management", "personnel_manage", "access_control", "session_history"] },
    { key: "workspace",    label: "Workspace & Team",    icon: LayoutDashboard, featureKeys: ["dashboard", "team", "performance"] },
    { key: "project",      label: "Project & Task",      icon: FolderKanban,    featureKeys: ["projects", "view_all_projects", "view_assigned_projects", "project_financials", "monitoring_wo", "tasks"] },
    { key: "tender",       label: "Tender & Pipeline (deprecated)",   icon: TrendingUp,      featureKeys: [] },
    { key: "financial",    label: "Keuangan (Laporan & Petty Cash)", icon: Wallet,       featureKeys: ["finance_report", "collection", "petty_cash"], iconAccent: "text-emerald-600" },
    // 2026-10-21 (user request): modul SDM lengkap — Kehadiran & Perizinan,
    // Cuti, Kalender Cuti, Payroll (sebelumnya payroll di modul Keuangan dan
    // Kehadiran jatuh ke bucket "Lainnya").
    { key: "hr",           label: "SDM — Kehadiran, Cuti & Payroll", icon: Briefcase,  featureKeys: ["attendance", "leaves", "leave_calendar", "payroll_print"] },
    { key: "assets",       label: "Asset Management",    icon: Package,         featureKeys: ["assets", "asset_locations"] },
    { key: "critical",     label: "Critical Issues & Decision Log", icon: AlertOctagon, featureKeys: ["critical_issues", "critical_issue_decisions", "critical_issue_categories"] },
    { key: "comm",         label: "Communication & Reminder", icon: Bell,       featureKeys: ["messages", "announcements", "reminders", "reminder_categories", "wo_reminders"] },
    { key: "settings",     label: "System Settings",     icon: Shield,          featureKeys: ["settings"] },
    // Fallback bucket untuk fitur baru yg belum di-map — biar tidak hilang dari UI.
    { key: "other",        label: "Lainnya",             icon: FileClock,       featureKeys: [] },
  ]), []);

  // Sensitive perm keys — sync dgn backend SENSITIVE_PERM_KEYS.
  const SENSITIVE_PERM_MAP = useMemo(() => ({
    petty_cash: new Set(["self_approve", "edit_all", "reopen", "delete", "confirm_transfer"]),
    // 149-cw-r170: close project/WO = aksi terminal — override butuh alasan (sync backend SENSITIVE_PERM_KEYS).
    projects: new Set(["close"]),
  }), []);

  // Dependency map — permission yg butuh perm lain agar backend tidak reject
  // (mirror invariants di server.py sanitizer).
  const DEPENDENCY_MAP = useMemo(() => ({
    // key: perm yang di-enable, value: array perm yg butuh ada juga (di feature yg sama)
    petty_cash: {
      approve: ["view"],
      reject: ["view"],
      edit_own: ["view"],
      edit_all: ["view"],
      assign_approver: ["view"],
      reopen: ["view"],
      verify_actual: ["view"],
      check: ["view"],
      confirm_transfer: ["view"],
      export: ["view"],
      self_approve: ["view", "approve"],
      delete: ["view"],
      restore: ["view"],
    },
    // 2026-10-16: input status staf lain butuh view kehadiran agar halaman terbaca.
    attendance: {
      enter_for_others: ["view"],
      approve: ["view"],
    },
    // 2026-10-21 (user request SDM): granular perms SDM & fitur lain — semua
    // butuh `view` agar halaman terkait terbaca.
    leaves: {
      backdate: ["view"],
    },
    payroll_print: {
      print_others: ["view"],
    },
    collection: {
      complete_identity: ["view"],
    },
    announcements: {
      broadcast: ["view"],
    },
    // 149-cw-r170: close butuh view supaya user bisa lihat project yg di-close.
    projects: {
      close: ["view"],
      edit_cost_control: ["view"],
    },
    // 2026-10-17: Isi Due Date butuh view agar halaman keuangan/collection terbaca.
    finance_report: {
      fill_due_date: ["view"],
    },
  }), []);

  const isStaffMode = targetMode === "staff";

  // Inherited baseline lookup — untuk Staff Spesifik yg belum punya override,
  // fallback ke rule Divisi+Level dari staff bersangkutan.
  // Fix 2026-02-15 (bug clarita): sebelumnya cell tampil semua-OFF ketika belum
  // ada override → admin kira staff tidak punya akses & auto-create override
  // yang divergent dari div+level. Sekarang cell menampilkan inherited value.
  const getInheritedCell = (row, staff) => {
    if (!staff) return {};
    const perms = row?.perms || {};
    return ((perms[staff.division] || {})[String(staff.level)]) || {};
  };
  const getInheritedScope = (row, staff) => {
    if (!staff) return "own";
    const vis = row?.visibility || {};
    const sc = ((vis[staff.division] || {})[String(staff.level)]);
    return sc || "own";
  };

  // 16-AC-MATRIX-UI-R2 (2026-08-26): tri-state helper untuk staff override.
  // newState = "default" | "allow" | "deny".
  // - default → hapus key dari user_perms & user_perms_deny (kembali ke inherited)
  // - allow   → set user_perms[uid][key]=true, hapus dari user_perms_deny
  // - deny    → set user_perms_deny[uid][key]=true, hapus dari user_perms
  const setStaffOverrideState = (featureKey, permKey, newState) => {
    if (!isStaffMode || !activeUserId) return;
    setRows((s) => s.map((r) => {
      if (r.feature !== featureKey) return r;
      const up = { ...(r.user_perms || {}) };
      const ud = { ...(r.user_perms_deny || {}) };
      const upCell = { ...(up[activeUserId] || {}) };
      const udCell = { ...(ud[activeUserId] || {}) };
      // Bersihkan key dari kedua cell dulu
      delete upCell[permKey];
      delete udCell[permKey];
      if (newState === "allow") upCell[permKey] = true;
      if (newState === "deny") udCell[permKey] = true;
      // Kalau cell kosong sepenuhnya, drop key uid supaya backend juga bersih
      if (Object.keys(upCell).length === 0) delete up[activeUserId];
      else up[activeUserId] = upCell;
      if (Object.keys(udCell).length === 0) delete ud[activeUserId];
      else ud[activeUserId] = udCell;
      return { ...r, user_perms: up, user_perms_deny: ud };
    }));
  };

  // Cek override state untuk (feature, permKey) di active user:
  //   "allow" / "deny" / "default"
  const getOverrideState = (row, permKey) => {
    if (!isStaffMode || !activeUserId) return "default";
    const upCell = (row.user_perms || {})[activeUserId] || {};
    const udCell = (row.user_perms_deny || {})[activeUserId] || {};
    if (udCell[permKey]) return "deny";
    if (upCell[permKey]) return "allow";
    return "default";
  };

  // Hitung permission efektif utk display (tri-state applied):
  //   effective = inherited (div×level) + allow overrides + deny overrides
  const getEffectiveCell = (row, staff) => {
    const base = getInheritedCell(row, staff);
    if (!staff?.id) return base;
    const allow = (row.user_perms || {})[staff.id] || {};
    const deny = (row.user_perms_deny || {})[staff.id] || {};
    const eff = { ...base };
    Object.entries(allow).forEach(([k, v]) => { if (v) eff[k] = true; });
    Object.entries(deny).forEach(([k, v]) => { if (v) eff[k] = false; });
    return eff;
  };

  const updateCell = (featureKey, perm, value) => {
    setRows((s) => s.map((r) => {
      if (r.feature !== featureKey) return r;
      if (isStaffMode) {
        const up = { ...(r.user_perms || {}) };
        // Seed dari inherited (div+level) supaya toggling 1 permission tidak
        // reset permission lain jadi false. Kalau override sudah ada, pakai itu.
        const existing = up[activeUserId];
        const inherited = getInheritedCell(r, activeStaff);
        const base = existing || {
          active: !!inherited.active,
          create: !!inherited.create,
          update: !!inherited.update,
          delete: !!inherited.delete,
          view:   !!inherited.view,
        };
        up[activeUserId] = { ...base, [perm]: value };
        return { ...r, user_perms: up };
      }
      const perms = { ...(r.perms || {}) };
      const div = { ...(perms[activeDiv] || {}) };
      const prev = div[String(activeLevel)] || { active: false, create: false, update: false, delete: false, view: false };
      // 16-PETTY-CASH-APPROVAL-ACCESS-CONTROL-R1: preserve extended keys (approve/reject/dst)
      // saat toggle 1 permission — spread `prev` supaya extended keys tidak ter-reset.
      div[String(activeLevel)] = { ...prev, [perm]: value };
      perms[activeDiv] = div;
      return { ...r, perms };
    }));
  };

  const updateVisibility = (featureKey, scope) => {
    setRows((s) => s.map((r) => {
      if (r.feature !== featureKey) return r;
      if (isStaffMode) {
        // Kalau visibility diubah tapi user_perms belum ada, kita hanya
        // simpan user_visibility (biar backend tetap fall-back ke div+level
        // perms). Kalau perms sudah override, visibility juga ikut.
        const uv = { ...(r.user_visibility || {}) };
        uv[activeUserId] = scope;
        return { ...r, user_visibility: uv };
      }
      const vis = { ...(r.visibility || {}) };
      const div = { ...(vis[activeDiv] || {}) };
      div[String(activeLevel)] = scope;
      vis[activeDiv] = div;
      return { ...r, visibility: vis };
    }));
  };

  // 16-AC-MATRIX-UI-R2 FASE-2: helper untuk reset SATU permission ke Ikuti Default
  // (staff mode). Menghapus key dari user_perms & user_perms_deny; NO silent enable
  // permission lain. Dipakai untuk tombol reset per-perm.
  const resetOverrideForKey = (featureKey, permKey) => {
    if (!isStaffMode || !activeUserId) return;
    setRows((s) => s.map((r) => {
      if (r.feature !== featureKey) return r;
      const up = { ...(r.user_perms || {}) };
      const ud = { ...(r.user_perms_deny || {}) };
      const upCell = { ...(up[activeUserId] || {}) };
      const udCell = { ...(ud[activeUserId] || {}) };
      delete upCell[permKey];
      delete udCell[permKey];
      if (Object.keys(upCell).length === 0) delete up[activeUserId]; else up[activeUserId] = upCell;
      if (Object.keys(udCell).length === 0) delete ud[activeUserId]; else ud[activeUserId] = udCell;
      return { ...r, user_perms: up, user_perms_deny: ud };
    }));
  };

  // 16-AC-MATRIX-UI-R2 FASE-2: dependency assistance — bila user mengaktifkan
  // permission yang butuh perm lain (mis. approve butuh view), tampilkan dialog
  // konfirmasi. TIDAK auto-enable diam-diam. User harus setuju dulu.
  // Berlaku hanya untuk staff-mode "allow" (deny dan default aman, dan mode
  // Divisi+Level pakai flow lama supaya tidak berubah default matrix).
  const checkDependencies = (featureKey, permKey, staff) => {
    const deps = (DEPENDENCY_MAP[featureKey] || {})[permKey];
    if (!deps || deps.length === 0) return [];
    const row = rows.find((r) => r.feature === featureKey);
    if (!row) return [];
    // Effective cell = base(div×level) + allow − deny. Cek key yang diperlukan.
    const eff = getEffectiveCell(row, staff);
    const missing = [];
    for (const req of deps) {
      if (!eff[req]) missing.push(req);
    }
    return missing;
  };
  const requestSetAllowWithDeps = (featureKey, permKey) => {
    if (!isStaffMode || !activeUserId) {
      setStaffOverrideState(featureKey, permKey, "allow");
      return;
    }
    const missing = checkDependencies(featureKey, permKey, activeStaff);
    if (missing.length === 0) {
      setStaffOverrideState(featureKey, permKey, "allow");
      return;
    }
    setDepsDialog({
      feature: featureKey,
      key: permKey,
      requires: missing,
      onConfirm: () => {
        // Aktifkan permKey + semua dependency yang belum aktif — ONLY setelah confirm
        setStaffOverrideState(featureKey, permKey, "allow");
        missing.forEach((req) => setStaffOverrideState(featureKey, req, "allow"));
        setDepsDialog(null);
      },
    });
  };

  // Quick-actions: mass toggle for the active target (div+level OR user)
  const applyAll = (value) => {
    setRows((s) => s.map((r) => {
      if (isStaffMode) {
        const up = { ...(r.user_perms || {}) };
        up[activeUserId] = { active: value, create: value, update: value, delete: value, view: value };
        return { ...r, user_perms: up };
      }
      const perms = { ...(r.perms || {}) };
      const div = { ...(perms[activeDiv] || {}) };
      div[String(activeLevel)] = { active: value, create: value, update: value, delete: value, view: value };
      perms[activeDiv] = div;
      return { ...r, perms };
    }));
  };

  // R3.2D.5 (2026-08-27): Quick-set checkboxes per module — SEJAJAR kolom tabel di bawah.
  // Kolom yg dipakai = PERM_LABELS (Aktif, Buat, Ubah, Hapus, Lihat) — cocok dgn kolom
  // permission per row di tabel di bawahnya. State per (moduleKey, permKey):
  //   - "on"    : setiap feature yg punya perm ini bernilai TRUE (izinkan)
  //   - "off"   : setiap feature yg punya perm ini bernilai FALSE / DENY (tolak)
  //   - "mixed" : selain 2 kondisi di atas (default/warisan atau campuran)
  //   - "na"    : perm tidak berlaku utk fitur apapun di modul → hide checkbox
  // Click: on → off (deny-all), off/mixed → on (allow-all, non-sensitif dilewati).
  const QUICK_SET_KEYS = PERM_LABELS; // {key: 'active'|'create'|'update'|'delete'|'view', label: 'Aktif'|'Buat'|...}
  const _permAvailableForFeature = (feature, permKey) => {
    // Semua feature standar mendukung 5 perm dasar (active/create/update/delete/view).
    // Petty cash juga punya perm ekstra tapi utk quick-set hanya 5 dasar yg relevan.
    // 149-cw-r170: `close` hanya berlaku untuk fitur projects.
    if (permKey === "close") return feature === "projects";
    return ["active","create","update","delete","view"].includes(permKey);
  };
  const computeQuickSetState = (moduleKey, permKey) => {
    const mod = MODULE_GROUPS.find((m) => m.key === moduleKey);
    if (!mod) return "mixed";
    const targetRows = rows.filter((r) => mod.featureKeys.includes(r.feature) && _permAvailableForFeature(r.feature, permKey));
    if (targetRows.length === 0) return "na";
    let allOn = true, allOff = true;
    for (const r of targetRows) {
      if (isStaffMode && activeUserId) {
        const up = (r.user_perms || {})[activeUserId] || {};
        const ud = (r.user_perms_deny || {})[activeUserId] || {};
        const inh = activeStaff ? getInheritedCell(r, activeStaff) : {};
        let effective;
        if (ud[permKey]) effective = false;
        else if (up[permKey]) effective = true;
        else effective = !!inh[permKey];
        if (effective) allOff = false; else allOn = false;
      } else {
        const cell = ((r.perms || {})[activeDiv] || {})[String(activeLevel)] || {};
        if (cell[permKey]) allOff = false; else allOn = false;
      }
    }
    if (allOn) return "on";
    if (allOff) return "off";
    return "mixed";
  };
  const applyQuickSet = (moduleKey, permKey, nextState) => {
    const mod = MODULE_GROUPS.find((m) => m.key === moduleKey);
    if (!mod) return;
    if (isStaffMode && !activeUserId) return;
    const featureSet = new Set(mod.featureKeys);
    setRows((s) => s.map((r) => {
      if (!featureSet.has(r.feature)) return r;
      if (!_permAvailableForFeature(r.feature, permKey)) return r;
      const isSensitive = _isSensitivePerm(r.feature, permKey);
      if (nextState === "on" && isSensitive) return r; // skip sensitive on bulk-allow
      if (isStaffMode) {
        const up = { ...(r.user_perms || {}) };
        const ud = { ...(r.user_perms_deny || {}) };
        const upCell = { ...(up[activeUserId] || {}) };
        const udCell = { ...(ud[activeUserId] || {}) };
        if (nextState === "on") {
          delete udCell[permKey];
          upCell[permKey] = true;
        } else {
          delete upCell[permKey];
          udCell[permKey] = true;
        }
        if (Object.keys(upCell).length === 0) delete up[activeUserId]; else up[activeUserId] = upCell;
        if (Object.keys(udCell).length === 0) delete ud[activeUserId]; else ud[activeUserId] = udCell;
        return { ...r, user_perms: up, user_perms_deny: ud };
      }
      const perms = { ...(r.perms || {}) };
      const div = { ...(perms[activeDiv] || {}) };
      const prev = div[String(activeLevel)] || {};
      const next = { ...prev, [permKey]: nextState === "on" };
      if (nextState === "on") next.active = true;
      div[String(activeLevel)] = next;
      perms[activeDiv] = div;
      return { ...r, perms };
    }));
  };

  // R3.2C: bulk action per-modul (scope explicit — hanya fitur di modul target).
  // Sensitive perm TIDAK ikut auto-enable — user harus set manual via popover.
  // 2026-10-21 (user request SDM): satu peta extended perm per fitur — sinkron
  // dgn FEATURE_EXTRA_PERMS di backend (perm granular tidak lagi hilang saat save).
  const EXTENDED_PERM_KEYS_BY_FEATURE = {
    petty_cash: ["approve","reject","edit_own","edit_all","assign_approver","reopen","verify_actual","confirm_transfer","export","restore","check"],
    attendance: ["approve", "enter_for_others"],
    leaves: ["backdate"],
    payroll_print: ["print_others"],
    collection: ["complete_identity"],
    finance_report: ["fill_due_date"],
    projects: ["edit_cost_control"],
    announcements: ["broadcast"],
  };
  const _relevantPermKeys = (feature) => {
    const base = feature === "petty_cash"
      ? ["active", "view", "create", "update", "delete"]
      : PERM_LABELS.map((p) => p.key).filter((k) => k !== "close" || feature === "projects");
    return [...base, ...(EXTENDED_PERM_KEYS_BY_FEATURE[feature] || [])];
  };
  const _isSensitivePerm = (feature, key) => (SENSITIVE_PERM_MAP[feature] || new Set()).has(key);
  const applyAllForModule = (moduleKey, value) => {
    const mod = MODULE_GROUPS.find((m) => m.key === moduleKey);
    if (!mod) return { changed: 0, sensitiveSkipped: 0 };
    const featureSet = new Set(mod.featureKeys);
    let changed = 0, sensitiveSkipped = 0;
    setRows((s) => s.map((r) => {
      if (!featureSet.has(r.feature)) return r;
      if (isStaffMode) {
        if (!activeUserId) return r;
        const up = { ...(r.user_perms || {}) };
        const ud = { ...(r.user_perms_deny || {}) };
        const upCell = { ...(up[activeUserId] || {}) };
        const udCell = { ...(ud[activeUserId] || {}) };
        for (const k of _relevantPermKeys(r.feature)) {
          if (value === true && _isSensitivePerm(r.feature, k)) { sensitiveSkipped++; continue; }
          delete udCell[k];
          if (value === true) upCell[k] = true; else delete upCell[k];
          changed++;
        }
        if (Object.keys(upCell).length === 0) delete up[activeUserId]; else up[activeUserId] = upCell;
        if (Object.keys(udCell).length === 0) delete ud[activeUserId]; else ud[activeUserId] = udCell;
        return { ...r, user_perms: up, user_perms_deny: ud };
      }
      // Divisi & Level mode
      const perms = { ...(r.perms || {}) };
      const div = { ...(perms[activeDiv] || {}) };
      const prev = div[String(activeLevel)] || {};
      const next = { ...prev };
      for (const k of _relevantPermKeys(r.feature)) {
        if (value === true && _isSensitivePerm(r.feature, k)) { sensitiveSkipped++; continue; }
        next[k] = value;
        changed++;
      }
      div[String(activeLevel)] = next;
      perms[activeDiv] = div;
      return { ...r, perms };
    }));
    return { changed, sensitiveSkipped };
  };
  const resetModuleForActive = (moduleKey) => {
    const mod = MODULE_GROUPS.find((m) => m.key === moduleKey);
    if (!mod) return { removed: 0 };
    const featureSet = new Set(mod.featureKeys);
    let removed = 0;
    setRows((s) => s.map((r) => {
      if (!featureSet.has(r.feature)) return r;
      if (isStaffMode) {
        if (!activeUserId) return r;
        const up = { ...(r.user_perms || {}) };
        const ud = { ...(r.user_perms_deny || {}) };
        const uv = { ...(r.user_visibility || {}) };
        removed += Object.keys(up[activeUserId] || {}).length + Object.keys(ud[activeUserId] || {}).length;
        delete up[activeUserId];
        delete ud[activeUserId];
        delete uv[activeUserId];
        return { ...r, user_perms: up, user_perms_deny: ud, user_visibility: uv };
      }
      // Divisi & Level mode — reset ke default matrix (empty cell = matrix akan re-seed defaults saat save).
      const perms = { ...(r.perms || {}) };
      const div = { ...(perms[activeDiv] || {}) };
      if (div[String(activeLevel)]) removed++;
      delete div[String(activeLevel)];
      perms[activeDiv] = div;
      return { ...r, perms };
    }));
    return { removed };
  };

  // Clear per-user override for the active user (revert to division/level rule)
  const clearStaffOverride = () => {
    if (!activeUserId) return;
    setRows((s) => s.map((r) => {
      const up = { ...(r.user_perms || {}) };
      const ud = { ...(r.user_perms_deny || {}) };
      const uv = { ...(r.user_visibility || {}) };
      delete up[activeUserId];
      delete ud[activeUserId];
      delete uv[activeUserId];
      return { ...r, user_perms: up, user_perms_deny: ud, user_visibility: uv };
    }));
    toast.info("Semua override staff dihapus (akan kembali ke rule divisi+level saat disimpan)");
  };

  // Copy From (src div,lv) → (activeDiv, activeLevel) — stage in setRows for all 14 features.
  const copyFrom = (srcDiv, srcLevel) => {
    setRows((s) => s.map((r) => {
      const perms = { ...(r.perms || {}) };
      const vis = { ...(r.visibility || {}) };
      const srcCell = (perms[srcDiv] || {})[String(srcLevel)];
      const srcScope = (vis[srcDiv] || {})[String(srcLevel)];
      const targetDivPerms = { ...(perms[activeDiv] || {}) };
      const targetDivVis = { ...(vis[activeDiv] || {}) };
      if (srcCell) targetDivPerms[String(activeLevel)] = { ...srcCell };
      else delete targetDivPerms[String(activeLevel)];
      if (srcScope) targetDivVis[String(activeLevel)] = srcScope;
      else delete targetDivVis[String(activeLevel)];
      perms[activeDiv] = targetDivPerms;
      vis[activeDiv] = targetDivVis;
      return { ...r, perms, visibility: vis };
    }));
  };

  // Feature filter (search)
  const [filter, setFilter] = useState("");

  // 16-AC-MATRIX-UI-R2 FASE-2: text-search + filter-chip. Search matches feature
  // key + label + permission key labels; auto-open accordion group untuk hasil.
  const searchLower = (filter || "").toLowerCase().trim();
  const isPermLabelMatch = (feature, key, label) => {
    if (!searchLower) return false;
    return (
      (feature || "").toLowerCase().includes(searchLower) ||
      (key || "").toLowerCase().includes(searchLower) ||
      (label || "").toLowerCase().includes(searchLower)
    );
  };

  // Row-level filter chip logic (staff mode).
  // - all       : no filter
  // - inherited : rows dgn NO override untuk active user (semuanya warisan)
  // - allow     : rows dgn min 1 key di user_perms[active]
  // - deny      : rows dgn min 1 key di user_perms_deny[active]
  // - changed   : dirty rows (dirtyKeys.has(feature))
  const filteredRows = useMemo(() => {
    let arr = rows;
    if (searchLower) {
      arr = arr.filter((r) => (r.feature || "").toLowerCase().includes(searchLower) || (r.label || "").toLowerCase().includes(searchLower));
    }
    return arr;
  }, [rows, searchLower]);

  const dirtyKeys = useMemo(() => {
    const initMap = new Map(initialRows.map((r) => [r.feature, r]));
    const set = new Set();
    for (const r of rows) {
      const init = initMap.get(r.feature);
      if (!init) continue;
      if (JSON.stringify(init.perms || {}) !== JSON.stringify(r.perms || {}) ||
          JSON.stringify(init.visibility || {}) !== JSON.stringify(r.visibility || {}) ||
          JSON.stringify(init.user_perms || {}) !== JSON.stringify(r.user_perms || {}) ||
          JSON.stringify(init.user_perms_deny || {}) !== JSON.stringify(r.user_perms_deny || {}) ||
          JSON.stringify(init.user_visibility || {}) !== JSON.stringify(r.user_visibility || {})) {
        set.add(r.feature);
      }
    }
    return set;
  }, [rows, initialRows]);

  // 16-AC-MATRIX-UI-R2 FASE-2: apply filter chip on top of search results.
  // Chip berlaku hanya di staff mode kecuali "changed" & "all" yang berlaku dua-duanya.
  const chipFilteredRows = useMemo(() => {
    if (filterChip === "all") return filteredRows;
    if (filterChip === "changed") return filteredRows.filter((r) => dirtyKeys.has(r.feature));
    if (!isStaffMode || !activeUserId) return filteredRows;
    return filteredRows.filter((r) => {
      const up = (r.user_perms || {})[activeUserId] || {};
      const ud = (r.user_perms_deny || {})[activeUserId] || {};
      const hasAllow = Object.values(up).some(Boolean);
      const hasDeny = Object.values(ud).some(Boolean);
      if (filterChip === "inherited") return !hasAllow && !hasDeny;
      if (filterChip === "allow") return hasAllow;
      if (filterChip === "deny") return hasDeny;
      return true;
    });
  }, [filteredRows, filterChip, dirtyKeys, isStaffMode, activeUserId]);

  // Group filtered rows by module (frontend-only). Fitur di luar mapping → "other".
  const groupedRows = useMemo(() => {
    const featureToModule = {};
    MODULE_GROUPS.forEach((g) => g.featureKeys.forEach((fk) => { featureToModule[fk] = g.key; }));
    const buckets = Object.fromEntries(MODULE_GROUPS.map((g) => [g.key, []]));
    for (const r of chipFilteredRows) {
      const modKey = featureToModule[r.feature] || "other";
      buckets[modKey].push(r);
    }
    return buckets;
  }, [chipFilteredRows, MODULE_GROUPS]);

  // Panel permission granular dirender DI DALAM accordion modul pemiliknya (portal ke slot).
  const [extSlots, setExtSlots] = useState({});
  const slotRefs = useMemo(() => Object.fromEntries(MODULE_GROUPS.map((g) => [g.key, (el) => {
    setExtSlots((s) => (s[g.key] === (el || undefined) ? s : { ...s, [g.key]: el || undefined }));
  }])), [MODULE_GROUPS]);
  const portalTo = (feature, node) => {
    const mod = MODULE_GROUPS.find((g) => g.featureKeys.includes(feature));
    const slot = mod && extSlots[mod.key];
    return slot ? createPortal(node, slot) : null;
  };

  // Auto-open accordion untuk group yang punya hasil search / chip filter aktif.
  // Kalau search kosong & chip=all → hormati state collapsedGroups (user preference).
  const autoOpenGroupKeys = useMemo(() => {
    if (!searchLower && filterChip === "all") return null; // pakai collapsedGroups apa adanya
    const s = new Set();
    Object.entries(groupedRows).forEach(([k, arr]) => { if (arr.length > 0) s.add(k); });
    return s;
  }, [searchLower, filterChip, groupedRows]);

  // 16-AC-MATRIX-UI-R2: sensitive perms yg butuh alasan wajib saat di-override
  const SENSITIVE_KEYS = useMemo(() => new Set([
    "petty_cash.self_approve",
    "petty_cash.edit_all",
    "petty_cash.reopen",
    "petty_cash.delete",
    "petty_cash.confirm_transfer",
  ]), []);

  // Detect apakah ada perubahan sensitif pending yg butuh alasan
  const sensitiveDeltas = useMemo(() => {
    const initMap = new Map(initialRows.map((r) => [r.feature, r]));
    const deltas = [];
    for (const r of rows) {
      const init = initMap.get(r.feature);
      if (!init) continue;
      const uidsSet = new Set([
        ...Object.keys(r.user_perms || {}),
        ...Object.keys(r.user_perms_deny || {}),
        ...Object.keys(init.user_perms || {}),
        ...Object.keys(init.user_perms_deny || {}),
      ]);
      for (const uid of uidsSet) {
        const newAllow = (r.user_perms || {})[uid] || {};
        const newDeny = (r.user_perms_deny || {})[uid] || {};
        const oldAllow = (init.user_perms || {})[uid] || {};
        const oldDeny = (init.user_perms_deny || {})[uid] || {};
        const allKeys = new Set([
          ...Object.keys(newAllow), ...Object.keys(newDeny),
          ...Object.keys(oldAllow), ...Object.keys(oldDeny),
        ]);
        for (const k of allKeys) {
          if (!SENSITIVE_KEYS.has(`${r.feature}.${k}`)) continue;
          const oldSt = oldAllow[k] ? "allow" : (oldDeny[k] ? "deny" : "default");
          const newSt = newAllow[k] ? "allow" : (newDeny[k] ? "deny" : "default");
          if (oldSt !== newSt) {
            deltas.push({ feature: r.feature, key: k, uid, oldSt, newSt });
          }
        }
      }
    }
    return deltas;
  }, [rows, initialRows, SENSITIVE_KEYS]);

  const [sensitiveDialog, setSensitiveDialog] = useState(null); // { reason, onConfirm }
  // R3.2C: konfirmasi bulk / reset per-modul
  const [moduleActionConfirm, setModuleActionConfirm] = useState(null); // {mode:"allow"|"deny"|"reset", moduleKey, moduleLabel}
  const [dirtyNavWarn, setDirtyNavWarn] = useState(null); // { onProceed }

  // R3.2C: guard untuk switch mode/user/div/level saat dirty.
  const attemptSwitch = useCallback((doSwitch) => {
    if (dirtyKeys.size === 0) { doSwitch(); return; }
    setDirtyNavWarn({ onProceed: () => { doSwitch(); setDirtyNavWarn(null); } });
  }, [dirtyKeys]);

  // R3.2C: beforeunload warning saat ada perubahan belum disimpan.
  useEffect(() => {
    if (dirtyKeys.size === 0) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirtyKeys]);

  const saveAll = async (overrideReason) => {
    if (dirtyKeys.size === 0) { toast.info("Tidak ada perubahan untuk disimpan"); return; }
    // 149-cw-r147 HARDENING: `overrideReason` HARUS string atau undefined. Kalau
    // caller lupa wrap (onClick={saveAll} → React SyntheticEvent lolos sbg arg),
    // treat itu sebagai "no reason provided" instead of crashing di .trim().
    const hasReason = typeof overrideReason === "string";
    // 16-AC-MATRIX-UI-R2 / 149-cw-r145 (2026-08-31): jika ada perubahan sensitif dan
    // caller belum menyediakan `overrideReason`, buka dialog konfirmasi. Setelah user
    // klik "Simpan dgn Alasan Ini", handler dialog akan panggil `saveAll(reason)`
    // langsung — TIDAK bergantung pada state `sensitiveDialog.confirmed` (yang
    // sebelumnya menyebabkan stale-closure bug: setTimeout(saveAll,50) memanggil
    // saveAll yang masih melihat confirmed=false → dialog re-open, tidak save).
    if (sensitiveDeltas.length > 0 && !hasReason) {
      setSensitiveDialog({
        deltas: sensitiveDeltas,
        reason: "",
      });
      return;
    }
    const reason = hasReason ? overrideReason.trim() : "";
    setSaving(true);
    let ok = 0, fail = 0;
    for (const r of rows) {
      if (!dirtyKeys.has(r.feature)) continue;
      try {
        const payload = {
          perms: r.perms || {},
          visibility: r.visibility || {},
          user_perms: r.user_perms || {},
          user_perms_deny: r.user_perms_deny || {},
          user_visibility: r.user_visibility || {},
        };
        // Kirim override_reason hanya kalau feature ini punya perubahan sensitif
        if (sensitiveDeltas.some((d) => d.feature === r.feature) && reason) {
          payload.override_reason = reason;
        }
        await api.patch(`/access-controls/${r.feature}`, payload);
        ok += 1;
      } catch (e) {
        fail += 1;
        toast.error(`Gagal menyimpan ${r.label}: ${formatDetail(e.response?.data?.detail)}`);
      }
    }
    setSaving(false);
    setSensitiveDialog(null);
    if (ok > 0) toast.success(`${ok} fitur berhasil disimpan${fail > 0 ? `, ${fail} gagal` : ""}`);
    await load();
    onSaved?.();
  };

  const resetChanges = () => {
    setRows(JSON.parse(JSON.stringify(initialRows)));
    toast.info("Perubahan dibatalkan");
  };

  if (loading) return <div className="text-xs text-slate-500 py-8 text-center">Memuat…</div>;

  const activeStaff = staffList.find((u) => u.id === activeUserId);
  // 16-AC-MATRIX-UI-R2: hitung badge counts (warisan / diizinkan khusus / ditolak khusus)
  const staffCounts = (() => {
    if (!activeUserId) return { inherited: 0, allow: 0, deny: 0 };
    let allow = 0, deny = 0, inherited = 0;
    for (const r of rows) {
      const upCell = (r.user_perms || {})[activeUserId] || {};
      const udCell = (r.user_perms_deny || {})[activeUserId] || {};
      const keys = new Set([...Object.keys(upCell), ...Object.keys(udCell)]);
      for (const k of keys) {
        if (udCell[k]) deny += 1;
        else if (upCell[k]) allow += 1;
      }
      const base = ((r.perms || {})[activeStaff?.division] || {})[String(activeStaff?.level)] || {};
      for (const k of Object.keys(base)) if (base[k] && !keys.has(k)) inherited += 1;
    }
    return { inherited, allow, deny };
  })();
  // "Locked" (BOD Lv1 super-admin) either by div/level mode or by staff belonging to BOD Lv1
  const isBod1 = isStaffMode
    ? (activeStaff?.division === "BOD" && Number(activeStaff?.level) === 1)
    : (activeDiv === "BOD" && activeLevel === 1);

  return (
    <section className="bg-white border border-slate-200 relative" data-testid="access-control-panel">
      {/* R3.2D: Header ringkas mengikuti mockup — hanya deskripsi 1 kalimat + tombol
          Preset & Import sebagai secondary action. Info teknis (matrix-driven,
          seed, last-admin) tersedia via tooltip di ikon info. */}
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="max-w-3xl leading-relaxed text-xs text-slate-600 flex items-start gap-2 min-w-0">
          <span>Atur hak akses berdasarkan divisi dan level pengguna, atau override untuk user tertentu.</span>
          <span
            className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-100 text-slate-500 text-[9px] font-bold cursor-help border border-slate-300 flex-shrink-0"
            data-testid="ac-info-tooltip"
            title="Permission ditentukan oleh Access Control Matrix. BOD Lv1 memperoleh permission awal melalui seed idempotent dan tetap dapat diubah. Sistem hanya mencegah perubahan yang meninggalkan nol user aktif dengan akses pengelolaan Access Control."
            aria-label="Info matrix-driven permission"
          >i</span>
        </div>
        <div className="flex items-center gap-1">
          <AccessPolicyPresets rows={rows} setRows={setRows} disabled={saving} />
        </div>
      </div>
      <div className="sr-only" data-testid="ac-bod-explanation">
        Permission ditentukan oleh Access Control Matrix. BOD Lv1 memperoleh permission awal melalui seed idempotent dan tetap dapat diubah. Sistem hanya mencegah perubahan yang meninggalkan nol user aktif dengan akses pengelolaan Access Control.
      </div>

      {/* R3.2D.3: Target Mode toggle — mengikuti mockup dgn label uppercase di atas + segmented control */}
      <div className="px-4 py-3 border-b border-slate-200 bg-white" data-testid="access-target-mode">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Mode Pengaturan</div>
        <div className="inline-flex border border-slate-300 rounded-md overflow-hidden">
          <button
            type="button"
            data-testid="access-mode-division"
            onClick={() => attemptSwitch(() => setTargetMode("division"))}
            aria-pressed={targetMode === "division"}
            className={"px-4 py-1.5 text-[11px] font-semibold border-r border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] transition-colors " + (targetMode === "division" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-700 hover:bg-slate-50")}
          >Divisi &amp; Level</button>
          <button
            type="button"
            data-testid="access-mode-staff"
            onClick={() => attemptSwitch(() => setTargetMode("staff"))}
            aria-pressed={targetMode === "staff"}
            className={"px-4 py-1.5 text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] transition-colors " + (targetMode === "staff" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-700 hover:bg-slate-50")}
          >Override User</button>
        </div>
      </div>

      {isStaffMode ? (
        /* R3.2D.4: Override User panel — spec-driven redesign.
           Card content = SINGLE ROW: [avatar] [dropdown+X] [division · Lv + Aktif] [3 mini-cards]
           Below card row (right-aligned): Reset button
           OUTSIDE card: info banner sky biru full-width */
        <div className="border-b border-slate-200 bg-white p-4" data-testid="access-staff-selector">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Pilih User</div>
          <div className="border border-slate-200 bg-white rounded-md p-4">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Avatar */}
              {activeStaff ? (
                <div className={`w-10 h-10 rounded-full ${DIVISION_COLOR[activeStaff.division] || "bg-slate-600 text-white"} inline-flex items-center justify-center text-sm font-bold flex-shrink-0`} aria-hidden="true">
                  {(activeStaff.name || "?").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-500 inline-flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <UsersIcon size={16} />
                </div>
              )}
              {/* Dropdown + X */}
              <div className="flex-1 min-w-[220px] max-w-[420px] relative">
                {/* 2026-10-06 (user request): dropdown Override User diganti
                    SearchableSelect — pencarian nama/email/divisi bisa diketik
                    (input di dalam Radix Select sebelumnya tidak menerima ketikan). */}
                <SearchableSelect
                  value={activeUserId}
                  onChange={(v) => attemptSwitch(() => setActiveUserId(v))}
                  options={staffList.map((u) => ({ value: u.id, label: `${u.name}${u.email ? ` · ${u.email}` : ""} — ${u.division} · Lv${u.level}` }))}
                  placeholder="Pilih staff…"
                  searchPlaceholder="Cari nama / email / divisi…"
                  emptyText="Tidak ada hasil"
                  testid="access-staff-dropdown"
                />
                {activeStaff && (
                  <button
                    type="button"
                    onClick={() => setActiveUserId("")}
                    className="absolute right-9 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                    aria-label="Clear user"
                    title="Clear"
                    data-testid="access-staff-clear"
                  ><X size={14} /></button>
                )}
              </div>
              {/* Division + Aktif */}
              {activeStaff && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Briefcase size={13} className="text-slate-500 flex-shrink-0" aria-hidden="true" />
                  <span className="text-[12px] text-slate-700 font-medium whitespace-nowrap">{activeStaff.division}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-[12px] text-slate-700 font-medium">Lv{activeStaff.level}</span>
                  <span className="ml-1 px-2 py-0.5 text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-800 rounded">Aktif</span>
                </div>
              )}
              {/* 3 mini summary cards — inline */}
              {activeStaff && (
                <div className="flex items-center gap-1.5 ml-auto" data-testid="access-badge-counts">
                  <div className="border border-slate-200 bg-slate-50 rounded px-2.5 py-1 text-center min-w-[70px]" data-testid="access-badge-inherited">
                    <div className="text-lg font-bold text-slate-700 leading-none">{staffCounts.inherited}</div>
                    <div className="text-[9px] tracking-wider text-slate-500 leading-tight mt-0.5">dari divisi</div>
                  </div>
                  <div className="border border-emerald-200 bg-emerald-50 rounded px-2.5 py-1 text-center min-w-[84px]" data-testid="access-badge-allow">
                    <div className="text-lg font-bold text-emerald-700 leading-none">{staffCounts.allow}</div>
                    <div className="text-[9px] tracking-wider text-emerald-700 leading-tight mt-0.5">diizinkan khusus</div>
                  </div>
                  <div className="border border-rose-200 bg-rose-50 rounded px-2.5 py-1 text-center min-w-[72px]" data-testid="access-badge-deny">
                    <div className="text-lg font-bold text-rose-700 leading-none">{staffCounts.deny}</div>
                    <div className="text-[9px] tracking-wider text-rose-700 leading-tight mt-0.5">ditolak khusus</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Below the card: info banner LEFT (flex-1) + Reset button RIGHT — one row */}
          <div className="mt-3 flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[280px] px-3 py-2 bg-sky-50 border border-sky-200 rounded text-[11px] text-sky-800 flex items-start gap-2" data-testid="ac-override-info-banner">
              <span className="w-4 h-4 rounded-full bg-sky-500 text-white text-[9px] font-bold inline-flex items-center justify-center flex-shrink-0" aria-hidden="true">i</span>
              <span>Permission user mengikuti Divisi &amp; Level, kecuali yang dioverride di halaman ini.</span>
            </div>
            {activeStaff && (
              <Button
                type="button"
                data-testid="access-clear-staff-override"
                onClick={() => setResetConfirm(true)}
                disabled={!activeUserId || (staffCounts.allow + staffCounts.deny === 0)}
                variant="outline"
                className="rounded-md h-9 text-[11px] border-slate-300 disabled:opacity-40 flex items-center gap-1.5 flex-shrink-0"
                title={
                  !activeUserId ? "Pilih user dulu"
                  : (staffCounts.allow + staffCounts.deny === 0) ? "Tidak ada override untuk user ini"
                  : `Hapus ${staffCounts.allow} izinkan khusus + ${staffCounts.deny} tolak khusus untuk ${activeStaff?.name || "user"}`
                }
              >
                <RotateCcw size={12} /> Reset semua override
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Division tabs */}
          <div className="flex flex-wrap border-b border-slate-200 bg-white" data-testid="access-div-tabs">
            {allDivisions.map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`access-div-tab-${d}`}
                onClick={() => attemptSwitch(() => setActiveDiv(d))}
                className={"px-4 py-2 text-[11px] uppercase tracking-wider font-semibold border-r border-slate-200 transition-colors " + (activeDiv === d ? "bg-[#0B1D3A] text-white" : "text-slate-600 hover:bg-slate-50")}
              >{d}</button>
            ))}
          </div>

          {/* Level tabs (bulk actions dipindah ke setiap accordion) */}
          <div className="flex items-stretch justify-between border-b border-slate-200 bg-slate-50">
            <div className="flex" data-testid="access-lv-tabs">
              {LV_LIST.map((lv) => (
                <button
                  key={lv}
                  type="button"
                  data-testid={`access-lv-tab-${lv}`}
                  onClick={() => attemptSwitch(() => setActiveLevel(lv))}
                  className={"px-5 py-2 text-[11px] uppercase tracking-wider font-semibold border-r border-slate-200 transition-colors " + (activeLevel === lv ? "bg-[#FF5A1F] text-white" : "text-slate-700 hover:bg-white")}
                >LV{lv}</button>
              ))}
            </div>
          </div>
        </>
      )}

      {isBod1 && (
        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 text-[11px] text-emerald-800" data-testid="access-bod-lv1-hint">
          <b>User ini adalah BOD Lv1 (Super Admin)</b> — permission diambil dari matrix (di-seed idempotent saat init, bukan runtime hardcode). Anda bebas mengubah cell BOD Lv1; sistem akan menolak (409) jika perubahan meninggalkan 0 user aktif dengan `access_control.update` (last-admin protection).
        </div>
      )}

      {/* Feature filter + Copy From */}
      {!isStaffMode && (
        <FeatureFilterAndCopy
          filter={filter}
          setFilter={setFilter}
          allDivisions={allDivisions}
          activeDiv={activeDiv}
          activeLevel={activeLevel}
          onCopy={copyFrom}
          disabled={isBod1}
        />
      )}
      {isStaffMode && (
        <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap" data-testid="ac-staff-filter-bar">
          <div className="relative flex-shrink-0">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Cari fitur atau permission…"
              aria-label="Cari fitur atau permission"
              className="border border-slate-300 h-9 pl-8 pr-3 text-[12px] w-72 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] focus:border-[#0B1D3A]"
              data-testid="ac-feature-search"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Filter permission">
            {[
              { key: "all", label: "Semua", cls: "bg-[#0B1D3A] text-white border-[#0B1D3A]" },
              { key: "inherited", label: "Warisan", cls: "bg-slate-100 text-slate-700 border-slate-300" },
              { key: "allow", label: "Diizinkan Khusus", cls: "bg-white text-emerald-700 border-emerald-400" },
              { key: "deny", label: "Ditolak Khusus", cls: "bg-white text-rose-700 border-rose-400" },
              { key: "changed", label: "Diubah", cls: "bg-slate-100 text-slate-700 border-slate-300" },
            ].map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={filterChip === c.key}
                onClick={() => setFilterChip(c.key)}
                data-testid={`ac-chip-${c.key}`}
                className={`px-3 py-1.5 text-[11px] font-semibold border rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] ${filterChip === c.key ? c.cls : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
              >{c.label}</button>
            ))}
          </div>
          <span className="text-[11px] text-slate-500 ml-auto" data-testid="ac-dirty-counter-inline">
            {dirtyKeys.size > 0 ? (
              <><b className="text-amber-700">{dirtyKeys.size}</b> perubahan belum disimpan</>
            ) : (
              <span className="text-slate-400">0 perubahan belum disimpan</span>
            )}
          </span>
        </div>
      )}

      {/* R3.2D: shared sticky column header dihapus mengikuti mockup — pola sekarang
          adalah module accordion dgn compact local grid. Tidak ada global table
          header. Column header lokal boleh muncul di dalam setiap accordion tapi
          TIDAK sticky, tidak menempel ke topbar. */}

      {/* Feature matrix */}
      <div className="min-w-0 w-full" data-testid="ac-matrix-container">
        {MODULE_GROUPS.map((mod) => {
          const modRows = groupedRows[mod.key] || [];
          if (modRows.length === 0) return null;
          const isOpen = autoOpenGroupKeys ? autoOpenGroupKeys.has(mod.key) : !collapsedGroups[mod.key];
          const dirtyCount = modRows.filter((r) => dirtyKeys.has(r.feature)).length;
          const toggle = () => setCollapsedGroups((s) => ({ ...s, [mod.key]: isOpen }));
          return (
            <section key={mod.key} className="border-t border-slate-200" data-testid={`ac-module-${mod.key}`}>
              <div className="w-full flex items-center gap-2 px-4 py-3 bg-white hover:bg-slate-50 flex-wrap">
                <button
                  type="button"
                  onClick={toggle}
                  aria-expanded={isOpen}
                  aria-controls={`ac-module-body-${mod.key}`}
                  data-testid={`ac-module-toggle-${mod.key}`}
                  className="flex items-center gap-2.5 flex-1 min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]"
                >
                  {mod.icon && (
                    <span className="w-8 h-8 rounded-md bg-slate-100 inline-flex items-center justify-center flex-shrink-0" aria-hidden="true">
                      <mod.icon size={16} className={mod.iconAccent || "text-slate-600"} strokeWidth={2} />
                    </span>
                  )}
                  <span className="text-[13px] font-bold text-[#0B1D3A]">{mod.label}</span>
                  <span className="text-[11px] text-slate-500 ml-1">{modRows.length} {isStaffMode ? "permission" : "fitur"}</span>
                  {dirtyCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold uppercase bg-amber-100 text-amber-800 border border-amber-300 rounded" title={`${dirtyCount} fitur belum disimpan`}>
                      <span aria-hidden="true">●</span> {dirtyCount} ubah
                    </span>
                  )}
                  <span aria-hidden="true" className="ml-auto text-slate-400 flex-shrink-0">{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
                </button>
                {isOpen && (
                  <div className="w-full flex items-center gap-1 justify-end" data-testid={`ac-module-bulk-${mod.key}`}>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setModuleActionConfirm({ mode: "allow", moduleKey: mod.key, moduleLabel: mod.label })}
                      disabled={isStaffMode && !activeUserId}
                      title={
                        isStaffMode && !activeUserId
                          ? "Pilih user dulu"
                          : `Izinkan semua permission non-sensitif untuk ${mod.label} (${modRows.length} fitur)`
                      }
                      data-testid={`ac-bulk-allow-${mod.key}`}
                      className="rounded-md h-7 text-[10px] disabled:opacity-40"
                    >
                      Izinkan Semua — {mod.label}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setModuleActionConfirm({ mode: "reset", moduleKey: mod.key, moduleLabel: mod.label })}
                      disabled={isStaffMode && !activeUserId}
                      title={`Reset ${mod.label} kembali ke Ikuti Default`}
                      data-testid={`ac-bulk-reset-${mod.key}`}
                      className="rounded-md h-7 text-[10px] disabled:opacity-40"
                    >
                      Reset Modul — {mod.label}
                    </Button>
                  </div>
                )}
              </div>
              {isOpen && (
              <div id={`ac-module-body-${mod.key}`} className="overflow-x-auto">
              {/* R3.2D.5 (rev): Quick-set row — POSISI SEJAJAR kolom checkbox di bawah.
                  Menggunakan colgroup identik dgn tabel utama (380px feature + 5×80px perm + rest),
                  sehingga setiap checkbox jatuh tepat di atas kolom perm-nya. */}
              <table className="w-full text-xs border-b border-slate-200 bg-slate-50" style={{ tableLayout: "fixed", minWidth: "1100px" }} data-testid={`ac-module-quickset-${mod.key}`}>
                <colgroup>
                  <col style={{ width: "380px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col />
                </colgroup>
                <tbody>
                  <tr>
                    <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Quick set:</td>
                    {QUICK_SET_KEYS.map((qs) => {
                      const state = computeQuickSetState(mod.key, qs.key);
                      const naState = state === "na";
                      const disabled = naState || (isStaffMode && !activeUserId);
                      const nextTarget = state === "on" ? "off" : "on";
                      const boxCls =
                        naState           ? "bg-slate-100 border-slate-200 text-slate-300" :
                        state === "on"    ? "bg-emerald-600 border-emerald-700 text-white" :
                        state === "off"   ? "bg-rose-600 border-rose-700 text-white" :
                        /* mixed */         "bg-white border-slate-400 text-slate-500";
                      const glyph = naState ? "" : (state === "on" ? "✓" : state === "off" ? "✕" : "–");
                      const title =
                        naState             ? "Tidak berlaku"
                        : disabled          ? "Pilih user dulu"
                        : state === "on"    ? `Klik untuk Tolak semua ${qs.label} pada modul ${mod.label}`
                        : state === "off"   ? `Klik untuk Izinkan semua ${qs.label} pada modul ${mod.label} (non-sensitif)`
                        :                     `Sebagian — klik untuk Izinkan semua ${qs.label} pada modul ${mod.label} (non-sensitif)`;
                      return (
                        <td key={qs.key} className="px-2 py-2 text-center align-middle">
                          <div className="flex flex-col items-center gap-1">
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={naState ? "false" : state === "on" ? "true" : state === "off" ? "false" : "mixed"}
                              aria-label={`Quick set ${qs.label} — ${state === "on" ? "semua izinkan" : state === "off" ? "semua tolak" : state === "mixed" ? "campuran" : "tidak berlaku"}`}
                              onClick={() => !disabled && applyQuickSet(mod.key, qs.key, nextTarget)}
                              disabled={disabled}
                              data-testid={`ac-quickset-${mod.key}-${qs.key}`}
                              data-state={state}
                              className={`w-5 h-5 border-2 inline-flex items-center justify-center text-[11px] font-bold rounded-sm transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] ${boxCls} ${disabled ? "opacity-70 cursor-not-allowed" : "hover:brightness-95"}`}
                              title={title}
                            >
                              <span aria-hidden="true">{glyph}</span>
                            </button>
                            <span className={`text-[9px] tracking-wider uppercase ${naState ? "text-slate-300" : "text-slate-500"}`}>{qs.label}</span>
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right text-[10px] italic text-slate-400">Sensitif dilewati saat bulk-izinkan · klik ulang untuk toggle</td>
                  </tr>
                </tbody>
              </table>
              <table className="w-full text-xs" style={{ tableLayout: "fixed", minWidth: "1100px" }}>
                <colgroup>
                  <col style={{ width: "380px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "80px" }} />
                  <col />
                </colgroup>
                {/* R3.2C.1: per-accordion thead dihapus dari sticky — hanya
                    accessible label (sr-only) untuk screen reader. Kolom header
                    visual ditangani oleh SATU shared sticky header di atas. */}
                <thead className="sr-only">
                  <tr>
                    <th>Fitur</th>
                    {PERM_LABELS.map((p) => (<th key={p.key}>{p.label}</th>))}
                    <th style={{ minWidth: "320px" }}>Visibilitas Data</th>
                  </tr>
                </thead>
                <tbody>
                  {modRows.map((r) => {
              // Staff Spesifik cell (2026-02-15 fix clarita bug):
              //  - Jika sudah ada `user_perms[userId]` → tampilkan itu (override aktif)
              //  - Jika belum ada → tampilkan inherited value dari div+level staff (baseline)
              //  Ini mencegah admin salah tafsir "semua OFF" & auto-create override divergent.
              const hasStaffOverride = isStaffMode && !!activeUserId && !!(r.user_perms || {})[activeUserId];
              const hasVisibilityOverride = isStaffMode && !!activeUserId && !!(r.user_visibility || {})[activeUserId];
              const inheritedCell = isStaffMode ? getInheritedCell(r, activeStaff) : {};
              const inheritedScope = isStaffMode ? getInheritedScope(r, activeStaff) : "own";
              const cell = isStaffMode
                ? (hasStaffOverride ? (r.user_perms || {})[activeUserId] : inheritedCell)
                : (((r.perms || {})[activeDiv] || {})[String(activeLevel)] || {});
              const scope = isStaffMode
                ? (hasVisibilityOverride ? (r.user_visibility || {})[activeUserId] : inheritedScope)
                : (((r.visibility || {})[activeDiv] || {})[String(activeLevel)] || "own");
              const active = !!cell.active;
              const dirty = dirtyKeys.has(r.feature);
              const isInherited = isStaffMode && !hasStaffOverride;
              return (
                <tr key={r.feature} className={"border-t border-slate-100 " + (dirty ? "bg-amber-50/50" : "")} data-testid={`access-row-${r.feature}`}>
                  <td className="px-3 py-3 align-top" style={{ overflowWrap: "anywhere" }}>
                    <div className="font-semibold text-[#0B1D3A] flex items-start gap-1.5 flex-wrap leading-snug">
                      <span className="whitespace-normal break-words" style={{ overflowWrap: "anywhere" }}>{r.label}</span>
                      {dirty && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5 flex-shrink-0" title="Belum disimpan" />}
                      {/* R3.2B (2026-08-26): Ringkasan per fitur — Warisan/Diizinkan/Ditolak counter (bukan single badge) */}
                      {isStaffMode && activeUserId && (() => {
                        const up = (r.user_perms || {})[activeUserId] || {};
                        const ud = (r.user_perms_deny || {})[activeUserId] || {};
                        // Semua permission key yang relevan untuk fitur ini
                        // (5 perm klasik + extended granular — sinkron backend).
                        const permKeys = _relevantPermKeys(r.feature);
                        let warisan = 0, izinkan = 0, tolak = 0;
                        for (const k of permKeys) {
                          if (ud[k]) tolak++;
                          else if (up[k]) izinkan++;
                          else warisan++;
                        }
                        return (
                          <span className="inline-flex items-center gap-1" data-testid={`access-summary-${r.feature}`}>
                            <span className="px-1 py-0 text-[8px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-300" title={`${warisan} action mengikuti default matrix`}>{warisan} Warisan</span>
                            {izinkan > 0 && <span className="px-1 py-0 text-[8px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300" title={`${izinkan} action Diizinkan Khusus untuk user ini`}>{izinkan} Diizinkan Khusus</span>}
                            {tolak > 0 && <span className="px-1 py-0 text-[8px] font-semibold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-300" title={`${tolak} action Ditolak Khusus untuk user ini`}>{tolak} Ditolak Khusus</span>}
                          </span>
                        );
                      })()}
                      {!isStaffMode && hasStaffOverride && <span className="px-1 py-0 text-[8px] font-semibold uppercase tracking-wider bg-indigo-100 text-indigo-800 border border-indigo-300" title="Ada override khusus staff ini — mengalahkan rule Divisi & Level" data-testid={`access-override-badge-${r.feature}`}>Override</span>}
                      {!isStaffMode && isInherited && activeStaff && (
                        <span className="px-1 py-0 text-[8px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-300" title={`Warisan dari ${activeStaff.division} Lv${activeStaff.level} — ubah untuk membuat override khusus staff ini`} data-testid={`access-inherited-badge-${r.feature}`}>Inherited</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono break-all" style={{ overflowWrap: "anywhere" }}>{r.feature}</div>
                  </td>
                  {PERM_LABELS.map((p) => {
                    // 149-cw-r170: perm `close` hanya berlaku utk fitur Projects —
                    // fitur lain render dash muted supaya tidak menyesatkan.
                    if (p.key === "close" && r.feature !== "projects") {
                      return (
                        <td key={p.key} className="text-center px-2 py-2 align-middle text-slate-300 select-none" title="Hanya berlaku untuk fitur Projects" data-testid={`access-perm-${r.feature}-${p.key}-na`}>—</td>
                      );
                    }
                    // For BOD Lv1, show all checked & disabled (super-admin)
                    const displayOn = isBod1 ? true : !!cell[p.key];
                    const muted = !isBod1 && p.key !== "active" && !active;
                    const disabled = isBod1 || (isStaffMode && !activeUserId);
                    // R3.2B-FOLLOW-UP (Full E): staff mode pakai Tri-State popover;
                    // div/level mode tetap binary checkbox (backend simpan di perms[div][lv]).
                    if (isStaffMode) {
                      const overrideState = getOverrideState(r, p.key); // "default"|"allow"|"deny"
                      const inheritedOn = !!inheritedCell[p.key];
                      const effectiveCellForRow = getEffectiveCell(r, activeStaff);
                      const effectiveOn = !!effectiveCellForRow[p.key];
                      const sensitiveThis = SENSITIVE_PERM_MAP[r.feature]?.has(p.key) || false;
                      const isSensitiveVisibilityDeny = false; // handled elsewhere
                      return (
                        <td key={p.key} className="text-center px-2 py-2 align-middle">
                          <TriStatePermCell
                            feature={r.feature}
                            permKey={p.key}
                            permLabel={p.label}
                            state={overrideState}
                            inheritedOn={inheritedOn}
                            effectiveOn={effectiveOn}
                            disabled={disabled}
                            sensitive={sensitiveThis}
                            testid={`access-perm-${r.feature}-${p.key}`}
                            onChange={(newState) => {
                              if (newState === "allow") {
                                // Route via dependency assistance dialog utk feature/perm ber-dep.
                                requestSetAllowWithDeps(r.feature, p.key);
                              } else {
                                setStaffOverrideState(r.feature, p.key, newState);
                              }
                            }}
                          />
                        </td>
                      );
                    }
                    // Divisi & Level mode → binary checkbox lama (tidak berubah).
                    const inheritedStyle = "";
                    return (
                      <td key={p.key} className="text-center px-2 py-2 align-middle">
                        <button
                          type="button"
                          data-testid={`access-perm-${r.feature}-${p.key}`}
                          onClick={() => !disabled && updateCell(r.feature, p.key, !cell[p.key])}
                          disabled={disabled}
                          className={"w-6 h-6 border inline-flex items-center justify-center transition-colors " +
                            (inheritedStyle
                              ? inheritedStyle
                              : (displayOn ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-slate-300 hover:border-slate-400")
                            ) +
                            (muted ? " opacity-40" : "") + (disabled ? " cursor-not-allowed" : "")
                          }
                          title={
                            isBod1 ? "BOD Lv1 super-admin"
                            : (muted ? "Aktif OFF — permission ini diabaikan" : "")
                          }
                        >{displayOn && <Check size={14} strokeWidth={3} />}
                        <span className="sr-only">{p.label}: {displayOn ? "aktif" : "tidak aktif"}</span>
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 align-top">
                    {r.scope_aware ? (
                      isStaffMode && activeUserId && !isBod1 ? (
                        // 16-AC-MATRIX-UI-R2 FASE-2: Visibility tri-state per user
                        // - Ikuti Default: no user_visibility[uid], no user_perms_deny.view
                        // - Scope khusus: user_visibility[uid] = scope value
                        // - Tolak akses: user_perms_deny[uid].view = true (backend enforced)
                        (() => {
                          const upDeny = (r.user_perms_deny || {})[activeUserId] || {};
                          const hasVis = !!(r.user_visibility || {})[activeUserId];
                          const isDenied = !!upDeny.view;
                          const state = isDenied ? "denied" : (hasVis ? "custom" : "default");
                          return (
                            <div className="space-y-1" data-testid={`access-vis-tristate-${r.feature}`} role="group" aria-label={`Visibility scope untuk ${r.label}`}>
                              <div className="flex gap-1 flex-wrap" role="radiogroup" aria-label="Mode visibility">
                                <button
                                  type="button"
                                  role="radio"
                                  aria-checked={state === "default"}
                                  data-testid={`access-vis-${r.feature}-default`}
                                  onClick={() => {
                                    setRows((s) => s.map((row) => {
                                      if (row.feature !== r.feature) return row;
                                      const uv = { ...(row.user_visibility || {}) };
                                      delete uv[activeUserId];
                                      const ud = { ...(row.user_perms_deny || {}) };
                                      const udCell = { ...(ud[activeUserId] || {}) };
                                      delete udCell.view;
                                      if (Object.keys(udCell).length === 0) delete ud[activeUserId]; else ud[activeUserId] = udCell;
                                      return { ...row, user_visibility: uv, user_perms_deny: ud };
                                    }));
                                  }}
                                  className={`flex-1 px-1.5 py-1 border text-[9px] uppercase tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] ${state === "default" ? "bg-slate-700 text-white border-slate-800" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                                >Ikuti Scope Default</button>
                                <button
                                  type="button"
                                  role="radio"
                                  aria-checked={state === "custom"}
                                  data-testid={`access-vis-${r.feature}-custom`}
                                  onClick={() => {
                                    // Set user_visibility to inherited scope as starting point, clear deny.view
                                    setRows((s) => s.map((row) => {
                                      if (row.feature !== r.feature) return row;
                                      const cur = (row.user_visibility || {})[activeUserId] || inheritedScope || "own";
                                      const uv = { ...(row.user_visibility || {}), [activeUserId]: cur };
                                      const ud = { ...(row.user_perms_deny || {}) };
                                      const udCell = { ...(ud[activeUserId] || {}) };
                                      delete udCell.view;
                                      if (Object.keys(udCell).length === 0) delete ud[activeUserId]; else ud[activeUserId] = udCell;
                                      return { ...row, user_visibility: uv, user_perms_deny: ud };
                                    }));
                                  }}
                                  className={`flex-1 px-1.5 py-1 border text-[9px] uppercase tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] ${state === "custom" ? "bg-sky-600 text-white border-sky-700" : "bg-white text-sky-700 border-sky-300 hover:bg-sky-50"}`}
                                >Atur Scope Khusus</button>
                                <button
                                  type="button"
                                  role="radio"
                                  aria-checked={state === "denied"}
                                  data-testid={`access-vis-${r.feature}-denied`}
                                  onClick={() => {
                                    // Sensitive: pergi ke sensitive dialog atau langsung set deny.view
                                    setRows((s) => s.map((row) => {
                                      if (row.feature !== r.feature) return row;
                                      const uv = { ...(row.user_visibility || {}) };
                                      delete uv[activeUserId];
                                      const ud = { ...(row.user_perms_deny || {}) };
                                      const udCell = { ...(ud[activeUserId] || {}), view: true };
                                      ud[activeUserId] = udCell;
                                      // Juga hapus allow.view kalau ada (mutually exclusive)
                                      const up = { ...(row.user_perms || {}) };
                                      const upCell = { ...(up[activeUserId] || {}) };
                                      delete upCell.view;
                                      if (Object.keys(upCell).length === 0) delete up[activeUserId]; else up[activeUserId] = upCell;
                                      return { ...row, user_visibility: uv, user_perms_deny: ud, user_perms: up };
                                    }));
                                  }}
                                  className={`flex-1 px-1.5 py-1 border text-[9px] uppercase tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] flex items-center justify-center gap-0.5 ${state === "denied" ? "bg-rose-600 text-white border-rose-700" : "bg-white text-rose-700 border-rose-300 hover:bg-rose-50"}`}
                                >
                                  <span aria-hidden="true">✕</span> Tolak Seluruh Visibilitas
                                </button>
                              </div>
                              {state === "custom" && (
                                <Select
                                  value={scope}
                                  onValueChange={(v) => updateVisibility(r.feature, v)}
                                  disabled={!active}
                                >
                                  <SelectTrigger data-testid={`access-scope-${r.feature}`} className="rounded-none h-7 text-[11px] disabled:opacity-60"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {SCOPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              )}
                              {state === "denied" && (
                                <div className="text-[9px] text-rose-700 italic" role="status">
                                  <span aria-hidden="true">⚠</span> User tidak dapat melihat data fitur ini (view=deny)
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <Select
                          value={isBod1 ? "all" : scope}
                          onValueChange={(v) => !isBod1 && (!isStaffMode || activeUserId) && updateVisibility(r.feature, v)}
                          disabled={isBod1 || !active || (isStaffMode && !activeUserId)}
                        >
                          <SelectTrigger data-testid={`access-scope-${r.feature}`} className="rounded-none h-7 text-[11px] disabled:opacity-60"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SCOPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
              )}
              {isOpen && <div ref={slotRefs[mod.key]} className="px-4 pb-4 empty:hidden" data-testid={`ac-module-ext-${mod.key}`} />}
            </section>
          );
        })}
      </div>

      {/* R3.2D: Weekly Petty Cash panel — 4 grouped panels (Akses Dasar / Persetujuan
          / Realisasi & Transfer / Pengelolaan Data) mengikuti mockup. Setiap
          permission row: label kiri + compact tri-state dropdown kanan + sumber
          default sebagai teks kecil di bawah. Layout responsif: 4 kolom desktop /
          2 tablet / 1 mobile. Semua 14 permission tetap ada. */}
      {(() => {
        const pcRow = filteredRows.find((x) => x.feature === "petty_cash");
        if (!pcRow) return null;
        const inheritedPcCell = isStaffMode ? getInheritedCell(pcRow, activeStaff) : {};
        const effectivePcCell = isStaffMode ? getEffectiveCell(pcRow, activeStaff) : (((pcRow.perms || {})[activeDiv] || {})[String(activeLevel)] || {});
        const pcCell = isStaffMode ? effectivePcCell : (((pcRow.perms || {})[activeDiv] || {})[String(activeLevel)] || {});
        const pcActive = !!pcCell.active;
        const disabled = isBod1 || (isStaffMode && !activeUserId);
        const dirty = dirtyKeys.has("petty_cash");
        const sourceLabel = isStaffMode
          ? (activeStaff ? `Dari ${activeStaff.division} · Lv${activeStaff.level}` : "Dari divisi/level")
          : `${activeDiv} · Lv${activeLevel}`;
        const PC_GROUPS = [
          { title: "1. Akses Dasar", perms: [
            { key: "view",     label: "Lihat pengajuan" },
            { key: "create",   label: "Buat pengajuan" },
            { key: "edit_own", label: "Edit pengajuan sendiri" },
            { key: "edit_all", label: "Edit semua pengajuan", sensitive: true },
            { key: "export",  label: "Export CSV / XLSX" },
          ]},
          { title: "2. Persetujuan", perms: [
            { key: "approve",         label: "Approve" },
            { key: "reject",          label: "Reject" },
            { key: "assign_approver", label: "Assign approver" },
            { key: "reopen",          label: "Buka kembali pengajuan", sensitive: true },
            { key: "self_approve",    label: "Self-approve", sensitive: true, hint: "Berisiko — nonaktif secara default; aktifkan hanya melalui permission khusus" },
          ]},
          { title: "3. Realisasi & Transfer", perms: [
            { key: "verify_actual",    label: "Verifikasi aktual" },
            { key: "confirm_transfer", label: "Konfirmasi transfer", sensitive: true },
            { key: "check",            label: "Check pasca-verifikasi", hint: "Tahapan terpisah setelah verifikasi — nama & tanggal pengecek tampil di kolom Check" },
          ]},
          { title: "4. Pengelolaan Data", perms: [
            { key: "delete",  label: "Hapus / Close / Cancel", sensitive: true, hint: "Juga me-gate tombol Close & Cancel pengajuan" },
            { key: "restore", label: "Pulihkan dari Trash" },
          ]},
        ];

        const renderRow = (p) => {
          const sensitive = !!p.sensitive;
          if (isStaffMode) {
            const state = getOverrideState(pcRow, p.key);
            const inheritedOn = !!inheritedPcCell[p.key];
            const effOn = !!effectivePcCell[p.key];
            const overridden = state !== "default";
            return (
              <div key={p.key} data-testid={`pc-perm-card-${p.key}`} className="py-2.5 border-b border-slate-100 last:border-0">
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(180px,220px)] gap-2 items-start">
                  <div className="min-w-0" style={{ overflowWrap: "anywhere" }}>
                    <div className="text-[12px] font-medium text-slate-800 leading-snug break-words">{p.label}</div>
                  </div>
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 min-w-0">
                        <TriStatePermCell
                          feature="petty_cash"
                          permKey={p.key}
                          permLabel={p.label}
                          state={state}
                          inheritedOn={inheritedOn}
                          effectiveOn={effOn}
                          disabled={disabled}
                          sensitive={sensitive}
                          wide
                          testid={`pc-perm-${p.key}`}
                          onChange={(newState) => {
                            if (newState === "allow") requestSetAllowWithDeps("petty_cash", p.key);
                            else setStaffOverrideState("petty_cash", p.key, newState);
                          }}
                        />
                      </div>
                      {overridden && (
                        <span
                          className="px-1.5 py-0.5 text-[9px] font-semibold bg-orange-100 text-orange-800 border border-orange-300 flex-shrink-0"
                          title={`Override user (${state === "allow" ? "Diizinkan" : "Ditolak"})`}
                        >Override user</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500">{sourceLabel}</div>
                    {sensitive && state === "deny" && (
                      <div className="text-[9px] text-amber-800 italic">Berisiko — ditolak khusus untuk user ini</div>
                    )}
                    {sensitive && state !== "deny" && (
                      <div className="text-[9px] text-amber-800 italic">
                        {p.hint || "Sensitif — butuh alasan saat save"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          // Divisi & Level mode
          const displayOn = isBod1 ? (p.key !== "self_approve" ? true : !!pcCell.self_approve) : !!pcCell[p.key];
          const muted = !isBod1 && !pcActive;
          return (
            <div key={p.key} data-testid={`pc-perm-card-${p.key}`} className={`py-2 border-b border-slate-100 last:border-0 ${muted ? "opacity-50" : ""}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1" style={{ overflowWrap: "anywhere" }}>
                  <div className="text-[12px] font-medium text-slate-800 leading-snug break-words">{p.label}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{sourceLabel}</div>
                  {sensitive && <div className="text-[9px] text-amber-800 mt-0.5 italic">{p.hint || "Sensitif"}</div>}
                </div>
                <button
                  type="button"
                  data-testid={`pc-perm-${p.key}`}
                  onClick={() => !disabled && updateCell("petty_cash", p.key, !pcCell[p.key])}
                  disabled={disabled}
                  aria-label={`${p.label}: ${displayOn ? "aktif" : "tidak aktif"}`}
                  aria-pressed={displayOn}
                  className={`w-7 h-7 border-2 inline-flex items-center justify-center flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] ${displayOn ? "bg-violet-600 border-violet-700 text-white" : "bg-white border-slate-400 hover:border-slate-600"} ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                >{displayOn && <Check size={14} strokeWidth={3} />}</button>
              </div>
            </div>
          );
        };

        return portalTo("petty_cash", (
          <div className="mt-3 border border-emerald-200 bg-white rounded-md" data-testid="pc-extended-perms-panel">
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2 flex-wrap rounded-t-md">
              <Wallet size={16} className="text-emerald-700" aria-hidden="true" />
              <span className="text-[13px] font-bold text-emerald-900">
                Weekly Petty Cash
              </span>
              <span className="text-[11px] px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold rounded" data-testid="pc-count-badge">
                14 permission
              </span>
              {dirty && <span className="text-[10px] uppercase text-amber-700 font-semibold">• Belum tersimpan</span>}
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-slate-600">Visibilitas data</span>
                <Select
                  value={((isStaffMode ? (pcRow.user_visibility || {})[activeUserId] : ((pcRow.visibility || {})[activeDiv] || {})[String(activeLevel)]) || "all")}
                  onValueChange={(v) => {
                    if (isBod1) return;
                    if (isStaffMode && !activeUserId) return;
                    updateVisibility("petty_cash", v);
                  }}
                  disabled={isBod1 || (isStaffMode && !activeUserId)}
                >
                  <SelectTrigger data-testid="pc-visibility-select" className="h-8 w-32 text-[11px] rounded-md border-slate-300 bg-white">
                    <SelectValue placeholder="Semua" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="division">Divisi</SelectItem>
                    <SelectItem value="division_lower">Divisi + Lower</SelectItem>
                    <SelectItem value="own">Sendiri</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-[11px] text-slate-500 pl-2 border-l border-emerald-200">
                  Target: <b>{isStaffMode ? (activeStaff?.name || "—") : `${activeDiv} Lv${activeLevel}`}</b>
                </span>
              </div>
            </div>
            {isStaffMode && (
              <div className="px-4 py-2 bg-white border-b border-slate-100 flex items-center gap-4 text-[11px] flex-wrap" data-testid="pc-legend">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-300"></span> Warisan</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Diizinkan khusus</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500"></span> Ditolak khusus</span>
              </div>
            )}
            <div
              className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3"
              data-testid="pc-grid"
              role="list"
              aria-label="14 permission Weekly Petty Cash dalam 4 grouped panels"
            >
              {PC_GROUPS.map((g) => (
                <div key={g.title} className="border border-slate-200 bg-white rounded-md flex flex-col" data-testid={`pc-group-${g.title.split(".")[0]}`}>
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between rounded-t-md">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">{g.title}</span>
                    <span className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-slate-200 text-slate-500 text-[9px] font-bold cursor-help" title={`${g.perms.length} permission dalam group ${g.title}`}>i</span>
                  </div>
                  <div className="px-3 flex-1">
                    {g.perms.map(renderRow)}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 pb-3 text-[10px] text-slate-500 italic">
              Backend adalah source of truth — endpoint validasi ulang setiap permission via <code className="bg-slate-100 px-1 border border-slate-200 rounded">_pc_can()</code>. Perubahan permission sensitif membutuhkan alasan wajib (dicatat di audit log).
            </div>
          </div>
        ));
      })()}
      {/* 2026-10-16 (user request): panel extended perm Kehadiran — "Input status
          kehadiran untuk staf lain" (enter_for_others). Mengikuti pola panel
          Weekly Petty Cash: tri-state di Staff Spesifik, checkbox di Divisi/Level. */}
      {(() => {
        const attRow = filteredRows.find((x) => x.feature === "attendance");
        if (!attRow) return null;
        const inheritedAttCell = isStaffMode ? getInheritedCell(attRow, activeStaff) : {};
        const effectiveAttCell = isStaffMode ? getEffectiveCell(attRow, activeStaff) : (((attRow.perms || {})[activeDiv] || {})[String(activeLevel)] || {});
        const attCell = isStaffMode ? effectiveAttCell : (((attRow.perms || {})[activeDiv] || {})[String(activeLevel)] || {});
        const attActive = !!attCell.active;
        const disabled = isBod1 || (isStaffMode && !activeUserId);
        const dirty = dirtyKeys.has("attendance");
        const sourceLabel = isStaffMode
          ? (activeStaff ? `Dari ${activeStaff.division} · Lv${activeStaff.level}` : "Dari divisi/level")
          : `${activeDiv} · Lv${activeLevel}`;
        // 2026-10-21 (user request SDM): tambah perm `approve` — reviewer
        // pengajuan (absensi/cuti/lembur/tugas luar) kini dapat diatur dari matrix.
        const PERMS = [
          { key: "approve", label: "Approve pengajuan (absensi, cuti, lembur, tugas luar)", hint: "Reviewer yang menyetujui/menolak pengajuan staf. Tanpa permission ini user hanya dapat melihat status pengajuan." },
          { key: "enter_for_others", label: "Input status kehadiran untuk staf lain", hint: "Contoh: sekretaris/HR mencatat staf lain sedang sakit — entri langsung berlaku di rekap kehadiran staf tersebut." },
        ];
        return portalTo("attendance", (
          <div className="mt-3 border border-sky-200 bg-white rounded-md" data-testid="att-extended-perms-panel">
            <div className="px-4 py-3 bg-sky-50 border-b border-sky-200 flex items-center gap-2 flex-wrap rounded-t-md">
              <span className="text-[13px] font-bold text-sky-900">Kehadiran</span>
              <span className="text-[11px] px-2 py-0.5 bg-sky-100 text-sky-800 border border-sky-300 font-semibold rounded" data-testid="att-count-badge">2 permission</span>
              {dirty && <span className="text-[10px] uppercase text-amber-700 font-semibold">• Belum tersimpan</span>}
              <div className="ml-auto text-[11px] text-slate-600">
                Target: <b>{isStaffMode ? (activeStaff?.name || "—") : `${activeDiv} Lv${activeLevel}`}</b>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {PERMS.map((PERM) => isStaffMode ? (
                <div key={PERM.key} data-testid={`att-perm-card-${PERM.key}`} className="py-1">
                  <div className="grid grid-cols-[minmax(0,1fr)_minmax(180px,220px)] gap-2 items-start">
                    <div className="min-w-0" style={{ overflowWrap: "anywhere" }}>
                      <div className="text-[12px] font-medium text-slate-800 leading-snug break-words">{PERM.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{PERM.hint}</div>
                    </div>
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 min-w-0">
                          <TriStatePermCell
                            feature="attendance"
                            permKey={PERM.key}
                            permLabel={PERM.label}
                            state={getOverrideState(attRow, PERM.key)}
                            inheritedOn={!!inheritedAttCell[PERM.key]}
                            effectiveOn={!!effectiveAttCell[PERM.key]}
                            disabled={disabled}
                            wide
                            testid={`att-perm-${PERM.key}`}
                            onChange={(newState) => {
                              if (newState === "allow") requestSetAllowWithDeps("attendance", PERM.key);
                              else setStaffOverrideState("attendance", PERM.key, newState);
                            }}
                          />
                        </div>
                        {getOverrideState(attRow, PERM.key) !== "default" && (
                          <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-orange-100 text-orange-800 border border-orange-300 flex-shrink-0">Override user</span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500">{sourceLabel}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div data-testid={`att-perm-card-${PERM.key}`} className={`py-1 ${!attActive && !isBod1 ? "opacity-50" : ""}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1" style={{ overflowWrap: "anywhere" }}>
                      <div className="text-[12px] font-medium text-slate-800 leading-snug break-words">{PERM.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{PERM.hint}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{sourceLabel}</div>
                    </div>
                    <button
                      type="button"
                      data-testid={`att-perm-${PERM.key}`}
                      onClick={() => !disabled && updateCell("attendance", PERM.key, !attCell[PERM.key])}
                      disabled={disabled}
                      aria-label={`${PERM.label}: ${(isBod1 || attCell[PERM.key]) ? "aktif" : "tidak aktif"}`}
                      aria-pressed={isBod1 || !!attCell[PERM.key]}
                      className={`w-7 h-7 border-2 inline-flex items-center justify-center flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] ${(isBod1 || attCell[PERM.key]) ? "bg-violet-600 border-violet-700 text-white" : "bg-white border-slate-400 hover:border-slate-600"} ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    >{(isBod1 || attCell[PERM.key]) && <Check size={14} strokeWidth={3} />}</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 pb-3 text-[10px] text-slate-500 italic">
              Backend adalah source of truth — endpoint <code className="bg-slate-100 px-1 border border-slate-200 rounded">/attendance/*</code> memvalidasi ulang permission ini di setiap request.
            </div>
          </div>
        ));
      })()}
      {/* 2026-10-17 (user request): panel extended perm Laporan Keuangan —
          "Isi Due Date" (fill_due_date). Mengikuti pola panel Kehadiran. */}
      {(() => {
        const frRow = filteredRows.find((x) => x.feature === "finance_report");
        if (!frRow) return null;
        const inheritedFrCell = isStaffMode ? getInheritedCell(frRow, activeStaff) : {};
        const effectiveFrCell = isStaffMode ? getEffectiveCell(frRow, activeStaff) : (((frRow.perms || {})[activeDiv] || {})[String(activeLevel)] || {});
        const frCell = isStaffMode ? effectiveFrCell : (((frRow.perms || {})[activeDiv] || {})[String(activeLevel)] || {});
        const frActive = !!frCell.active;
        const disabled = isBod1 || (isStaffMode && !activeUserId);
        const dirty = dirtyKeys.has("finance_report");
        const sourceLabel = isStaffMode
          ? (activeStaff ? `Dari ${activeStaff.division} · Lv${activeStaff.level}` : "Dari divisi/level")
          : `${activeDiv} · Lv${activeLevel}`;
        const PERM = { key: "fill_due_date", label: "Isi Due Date (bulk edit)", hint: "Memasang tanggal jatuh tempo massal pada invoice yang masih kosong — dari Monitoring Proyek (kartu Jatuh Tempo Belum Diisi) & Collection. Audit log dicatat per invoice." };
        return portalTo("finance_report", (
          <div className="mt-3 border border-emerald-200 bg-white rounded-md" data-testid="finance-extended-perms-panel">
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2 flex-wrap rounded-t-md">
              <span className="text-[13px] font-bold text-emerald-900">Laporan Keuangan — Isi Due Date</span>
              <span className="text-[11px] px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold rounded" data-testid="finance-count-badge">1 permission</span>
              {dirty && <span className="text-[10px] uppercase text-amber-700 font-semibold">• Belum tersimpan</span>}
              <div className="ml-auto text-[11px] text-slate-600">
                Target: <b>{isStaffMode ? (activeStaff?.name || "—") : `${activeDiv} Lv${activeLevel}`}</b>
              </div>
            </div>
            <div className="p-4">
              {isStaffMode ? (
                <div data-testid={`fr-perm-card-${PERM.key}`} className="py-1">
                  <div className="grid grid-cols-[minmax(0,1fr)_minmax(180px,220px)] gap-2 items-start">
                    <div className="min-w-0" style={{ overflowWrap: "anywhere" }}>
                      <div className="text-[12px] font-medium text-slate-800 leading-snug break-words">{PERM.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{PERM.hint}</div>
                    </div>
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 min-w-0">
                          <TriStatePermCell
                            feature="finance_report"
                            permKey={PERM.key}
                            permLabel={PERM.label}
                            state={getOverrideState(frRow, PERM.key)}
                            inheritedOn={!!inheritedFrCell[PERM.key]}
                            effectiveOn={!!effectiveFrCell[PERM.key]}
                            disabled={disabled}
                            wide
                            testid={`fr-perm-${PERM.key}`}
                            onChange={(newState) => {
                              if (newState === "allow") requestSetAllowWithDeps("finance_report", PERM.key);
                              else setStaffOverrideState("finance_report", PERM.key, newState);
                            }}
                          />
                        </div>
                        {getOverrideState(frRow, PERM.key) !== "default" && (
                          <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-orange-100 text-orange-800 border border-orange-300 flex-shrink-0">Override user</span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500">{sourceLabel}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div data-testid={`fr-perm-card-${PERM.key}`} className={`py-1 ${!frActive && !isBod1 ? "opacity-50" : ""}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1" style={{ overflowWrap: "anywhere" }}>
                      <div className="text-[12px] font-medium text-slate-800 leading-snug break-words">{PERM.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{PERM.hint}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{sourceLabel}</div>
                    </div>
                    <button
                      type="button"
                      data-testid={`fr-perm-${PERM.key}`}
                      onClick={() => !disabled && updateCell("finance_report", PERM.key, !frCell[PERM.key])}
                      disabled={disabled}
                      aria-label={`${PERM.label}: ${(isBod1 || frCell[PERM.key]) ? "aktif" : "tidak aktif"}`}
                      aria-pressed={isBod1 || !!frCell[PERM.key]}
                      className={`w-7 h-7 border-2 inline-flex items-center justify-center flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] ${(isBod1 || frCell[PERM.key]) ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-slate-400 hover:border-slate-600"} ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    >{(isBod1 || frCell[PERM.key]) && <Check size={14} strokeWidth={3} />}</button>
                  </div>
                </div>
              )}
            </div>
            <div className="px-4 pb-3 text-[10px] text-slate-500 italic">
              Backend adalah source of truth — endpoint <code className="bg-slate-100 px-1 border border-slate-200 rounded">/active-projects/income/bulk-due-date</code> memvalidasi ulang permission ini di setiap request.
            </div>
          </div>
        ));
      })()}
      {/* 2026-10-21 (user request "PERBAHARUI ACCESS CONTROL MATRIX — SDM"):
          panel extended perm generik untuk granular perms fitur SDM & lainnya
          (Cuti backdate, Payroll print_others, Collection complete_identity,
          Pengumuman broadcast). Pola identik dgn panel Kehadiran/Finance di atas:
          tri-state di Staff Spesifik, checkbox di Divisi/Level. */}
      {[
        { feature: "projects", title: "Cost Control", prefix: "proj", tone: "sky",
          note: <>Endpoint <code className="bg-slate-100 px-1 border border-slate-200 rounded">/active-projects/:pid/cost-control/*</code> (import, tambah, ubah, hapus, pindah baris) memvalidasi ulang permission ini di setiap request.</>,
          perms: [{ key: "edit_cost_control", label: "Import Excel & Edit Baris Cost Control", hint: "Menampilkan tombol Import Excel serta tambah/edit/hapus/pindah baris Item Pekerjaan & Overhead di halaman Cost Control. Butuh akses Lihat project." }] },
        { feature: "leaves", title: "Cuti & Izin", prefix: "leave", tone: "violet",
          note: <>Backend memvalidasi <code className="bg-slate-100 px-1 border border-slate-200 rounded">leaves.backdate</code> saat user mengisi tanggal pengajuan lampau — tercatat di audit trail.</>,
          perms: [{ key: "backdate", label: "Backdate tanggal pengajuan cuti/izin", hint: "Mengizinkan pengajuan dengan tanggal lampau (koreksi data historis)." }] },
        { feature: "payroll_print", title: "Payroll / Slip Gaji", prefix: "pay", tone: "violet",
          note: <>Tanpa permission ini user hanya dapat mencetak slip gaji miliknya sendiri.</>,
          perms: [{ key: "print_others", label: "Cetak slip gaji karyawan lain", hint: "Akses sensitif — slip gaji memuat data remunerasi personal." }] },
        { feature: "collection", title: "Collection — Identitas Invoice", prefix: "col", tone: "orange",
          note: <>Endpoint <code className="bg-slate-100 px-1 border border-slate-200 rounded">PATCH /collection/invoices/:pid/:eid/identity</code> memvalidasi ulang permission ini di setiap request. Perubahan identitas TIDAK mengubah nilai invoice/pembayaran/saldo.</>,
          perms: [{ key: "complete_identity", label: "Lengkapi Identitas Invoice", hint: "Mengubah nomor invoice, referensi eksternal, tanggal invoice/jatuh tempo, PIC, dan tautan transaksi sumber dari antrean 'Invoice Perlu Dilengkapi'." }] },
        { feature: "announcements", title: "Pengumuman", prefix: "ann", tone: "sky",
          note: null,
          perms: [{ key: "broadcast", label: "Broadcast pengumuman ke seluruh organisasi", hint: "Tanpa permission ini pengumuman hanya terkirim ke scope divisi pengirim." }] },
      ].map((cfg) => {
        const row = filteredRows.find((x) => x.feature === cfg.feature);
        if (!row) return null;
        const inheritedCell = isStaffMode ? getInheritedCell(row, activeStaff) : {};
        const effectiveCell = isStaffMode ? getEffectiveCell(row, activeStaff) : (((row.perms || {})[activeDiv] || {})[String(activeLevel)] || {});
        const cell = isStaffMode ? effectiveCell : (((row.perms || {})[activeDiv] || {})[String(activeLevel)] || {});
        const featActive = !!cell.active;
        const disabled = isBod1 || (isStaffMode && !activeUserId);
        const dirty = dirtyKeys.has(cfg.feature);
        const sourceLabel = isStaffMode
          ? (activeStaff ? `Dari ${activeStaff.division} · Lv${activeStaff.level}` : "Dari divisi/level")
          : `${activeDiv} · Lv${activeLevel}`;
        const TONES = {
          violet: { wrap: "border-violet-200", head: "bg-violet-50 border-violet-200", title: "text-violet-900", badge: "bg-violet-100 text-violet-800 border-violet-300", check: "bg-violet-600 border-violet-700" },
          orange: { wrap: "border-orange-200", head: "bg-orange-50 border-orange-200", title: "text-orange-900", badge: "bg-orange-100 text-orange-800 border-orange-300", check: "bg-orange-600 border-orange-700" },
          sky: { wrap: "border-sky-200", head: "bg-sky-50 border-sky-200", title: "text-sky-900", badge: "bg-sky-100 text-sky-800 border-sky-300", check: "bg-sky-600 border-sky-700" },
        };
        const T = TONES[cfg.tone] || TONES.sky;
        return portalTo(cfg.feature, (
          <div key={cfg.feature} className={`mt-3 border bg-white rounded-md ${T.wrap}`} data-testid={`${cfg.feature}-extended-perms-panel`}>
            <div className={`px-4 py-3 border-b flex items-center gap-2 flex-wrap rounded-t-md ${T.head}`}>
              <span className={`text-[13px] font-bold ${T.title}`}>{cfg.title}</span>
              <span className={`text-[11px] px-2 py-0.5 border font-semibold rounded ${T.badge}`} data-testid={`${cfg.feature}-count-badge`}>{cfg.perms.length} permission</span>
              {dirty && <span className="text-[10px] uppercase text-amber-700 font-semibold">• Belum tersimpan</span>}
              <div className="ml-auto text-[11px] text-slate-600">
                Target: <b>{isStaffMode ? (activeStaff?.name || "—") : `${activeDiv} Lv${activeLevel}`}</b>
              </div>
            </div>
            <div className="p-4 space-y-2">
              {cfg.perms.map((PERM) => isStaffMode ? (
                <div key={PERM.key} data-testid={`${cfg.prefix}-perm-card-${PERM.key}`} className="py-1">
                  <div className="grid grid-cols-[minmax(0,1fr)_minmax(180px,220px)] gap-2 items-start">
                    <div className="min-w-0" style={{ overflowWrap: "anywhere" }}>
                      <div className="text-[12px] font-medium text-slate-800 leading-snug break-words">{PERM.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{PERM.hint}</div>
                    </div>
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 min-w-0">
                          <TriStatePermCell
                            feature={cfg.feature}
                            permKey={PERM.key}
                            permLabel={PERM.label}
                            state={getOverrideState(row, PERM.key)}
                            inheritedOn={!!inheritedCell[PERM.key]}
                            effectiveOn={!!effectiveCell[PERM.key]}
                            disabled={disabled}
                            wide
                            testid={`${cfg.prefix}-perm-${PERM.key}`}
                            onChange={(newState) => {
                              if (newState === "allow") requestSetAllowWithDeps(cfg.feature, PERM.key);
                              else setStaffOverrideState(cfg.feature, PERM.key, newState);
                            }}
                          />
                        </div>
                        {getOverrideState(row, PERM.key) !== "default" && (
                          <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-orange-100 text-orange-800 border border-orange-300 flex-shrink-0">Override user</span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500">{sourceLabel}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={PERM.key} data-testid={`${cfg.prefix}-perm-card-${PERM.key}`} className={`py-1 ${!featActive && !isBod1 ? "opacity-50" : ""}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1" style={{ overflowWrap: "anywhere" }}>
                      <div className="text-[12px] font-medium text-slate-800 leading-snug break-words">{PERM.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{PERM.hint}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{sourceLabel}</div>
                    </div>
                    <button
                      type="button"
                      data-testid={`${cfg.prefix}-perm-${PERM.key}`}
                      onClick={() => !disabled && updateCell(cfg.feature, PERM.key, !cell[PERM.key])}
                      disabled={disabled}
                      aria-label={`${PERM.label}: ${(isBod1 || cell[PERM.key]) ? "aktif" : "tidak aktif"}`}
                      aria-pressed={isBod1 || !!cell[PERM.key]}
                      className={`w-7 h-7 border-2 inline-flex items-center justify-center flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] ${(isBod1 || cell[PERM.key]) ? `${T.check} text-white` : "bg-white border-slate-400 hover:border-slate-600"} ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    >{(isBod1 || cell[PERM.key]) && <Check size={14} strokeWidth={3} />}</button>
                  </div>
                </div>
              ))}
            </div>
            {cfg.note && <div className="px-4 pb-3 text-[10px] text-slate-500 italic">{cfg.note}</div>}
          </div>
        ));
      })}
      {sensitiveDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="sensitive-confirm-dialog">
          <div className="bg-white border border-slate-300 max-w-lg w-full">
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
              <div className="font-bold text-amber-900 uppercase text-sm flex items-center gap-2">⚠ Konfirmasi Perubahan Permission Sensitif</div>
              <div className="text-[10px] text-amber-800 mt-1">Perubahan permission berikut memiliki dampak keamanan tinggi. Wajib mengisi alasan untuk audit trail.</div>
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Perubahan Terdeteksi ({sensitiveDeltas.length})</div>
              <ul className="text-xs text-slate-700 space-y-1 mb-3 max-h-40 overflow-auto border border-slate-200 p-2 bg-slate-50">
                {sensitiveDeltas.map((d, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 border-b border-slate-100 last:border-0 py-1" data-testid={`sensitive-delta-${i}`}>
                    <span className="font-mono text-[10px]">{d.feature}.{d.key}</span>
                    <span className="text-[10px]"><span className="line-through text-slate-400">{d.oldSt}</span> → <span className="font-bold text-rose-700">{d.newSt}</span></span>
                  </li>
                ))}
              </ul>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Alasan Perubahan (wajib, min 5 karakter)</label>
              <textarea
                value={sensitiveDialog.reason || ""}
                onChange={(e) => setSensitiveDialog((d) => ({ ...d, reason: e.target.value }))}
                rows={3}
                className="w-full border border-slate-300 p-2 text-xs mt-1 rounded-none focus:outline-none focus:border-amber-500"
                placeholder="Contoh: Approver cadangan saat FAT Lv2 cuti panjang; disetujui via memo BOD 2026-08-26"
                data-testid="sensitive-reason-input"
              />
              <div className="flex items-center justify-end gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setSensitiveDialog(null)}
                  className="px-3 py-1.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-100"
                  data-testid="sensitive-cancel"
                >Batal</button>
                <button
                  type="button"
                  disabled={(sensitiveDialog.reason || "").trim().length < 5 || saving}
                  onClick={() => {
                    // 149-cw-r145: langsung panggil saveAll(reason) — no setTimeout,
                    // no state juggling. Bypass stale-closure issue.
                    const r = (sensitiveDialog.reason || "").trim();
                    if (r.length < 5) return;
                    saveAll(r);
                  }}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-40"
                  data-testid="sensitive-confirm"
                >{saving ? "Menyimpan…" : "Simpan dgn Alasan Ini"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {(dirtyKeys.size > 0) && (
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-30 flex items-center justify-between px-4 sm:px-6 py-3 gap-3 flex-wrap" data-testid="ac-sticky-action-bar" role="region" aria-label="Bar aksi simpan perubahan" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0" aria-hidden="true"></span>
            <span className="text-[12px] font-medium text-slate-800" data-testid="ac-action-bar-summary">
              <b data-testid="ac-action-bar-changed-count">{dirtyKeys.size}</b>{" "}
              {isStaffMode ? "override belum disimpan" : (
                <>perubahan untuk <b data-testid="ac-action-bar-target">{`${activeDiv} Lv${activeLevel}`}</b></>
              )}
            </span>
            {sensitiveDeltas.length > 0 && (
              <span
                className="px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300 rounded flex items-center gap-1"
                data-testid="ac-action-bar-sensitive-count"
                title="Permission sensitif membutuhkan alasan wajib saat save"
              >
                <span aria-hidden="true">⚠</span> {sensitiveDeltas.length} sensitif
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            <Button type="button" onClick={resetChanges} disabled={saving} variant="outline" className="rounded-md h-9 px-4 text-[12px] border-slate-300" data-testid="btn-sticky-cancel">
              Batalkan Perubahan
            </Button>
            <Button type="button" onClick={() => saveAll()} disabled={saving} className="rounded-md h-9 px-4 bg-[#0B1D3A] hover:bg-slate-800 text-white text-[12px] font-semibold flex items-center gap-1.5" data-testid="btn-sticky-save">
              {saving ? "Menyimpan…" : (isStaffMode ? "Simpan Override" : "Simpan Perubahan")}
            </Button>
          </div>
        </div>
      )}
      {/* Spacer supaya konten terakhir tidak tertutup sticky action bar */}
      {(dirtyKeys.size > 0) && <div className="h-4" aria-hidden="true" data-testid="ac-bottom-pad"></div>}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reset-override-dialog" role="dialog" aria-modal="true" aria-labelledby="reset-override-dialog-title">
          <div className="bg-white border border-slate-300 max-w-md w-full">
            <div className="px-4 py-3 bg-rose-50 border-b border-rose-200">
              <div id="reset-override-dialog-title" className="font-bold text-rose-900 uppercase text-sm"><span aria-hidden="true">⚠ </span>Reset Seluruh Override User</div>
              <div className="text-[10px] text-rose-800 mt-1">Menghapus <b>semua</b> override user untuk <b>{activeStaff?.name || "user ini"}</b> di <b>semua modul</b>. Setelah reset, permission user kembali mengikuti rule Divisi &amp; Level ({activeStaff?.division} Lv{activeStaff?.level}). Matrix Divisi &amp; Level TIDAK diubah.</div>
            </div>
            <div className="p-4">
              <div className="text-xs text-slate-700 mb-3">
                Anda akan menghapus <b className="text-emerald-700">{staffCounts.allow}</b> aksi diizinkan khusus + <b className="text-rose-700">{staffCounts.deny}</b> aksi ditolak khusus.
                <br/>Tindakan ini dicatat di audit log. Data user &amp; transaksi Petty Cash tidak berubah.
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setResetConfirm(false)} className="px-3 py-1.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]" data-testid="reset-cancel">Batal</button>
                <button onClick={() => { clearStaffOverride(); setResetConfirm(false); }} className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-[10px] uppercase tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]" data-testid="reset-confirm">Ya, Reset Semua Override</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* R3.2C: Konfirmasi bulk / reset per-modul */}
      {moduleActionConfirm && (() => {
        const { mode, moduleKey, moduleLabel } = moduleActionConfirm;
        const mod = MODULE_GROUPS.find((m) => m.key === moduleKey);
        const featureCount = mod ? mod.featureKeys.length : 0;
        const isAllow = mode === "allow";
        const isReset = mode === "reset";
        const titleText = isAllow ? `Izinkan Semua — ${moduleLabel}` : `Reset Modul — ${moduleLabel}`;
        const scopeText = isStaffMode
          ? `${activeStaff?.name || "user"} (${activeStaff?.division} Lv${activeStaff?.level})`
          : `${activeDiv} Lv${activeLevel}`;
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="module-action-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="module-action-title">
            <div className="bg-white border border-slate-300 max-w-md w-full">
              <div className={`px-4 py-3 ${isAllow ? "bg-emerald-50 border-b border-emerald-200" : "bg-slate-100 border-b border-slate-300"}`}>
                <div id="module-action-title" className={`font-bold uppercase text-sm ${isAllow ? "text-emerald-900" : "text-slate-800"}`}>{titleText}</div>
                <div className="text-[10px] mt-1 text-slate-700">
                  Scope: <b>modul {moduleLabel}</b> ({featureCount} fitur) · Target: <b>{scopeText}</b>
                </div>
              </div>
              <div className="p-4 text-xs text-slate-700 space-y-2">
                {isAllow && (
                  <>
                    <div>Semua permission non-sensitif di modul <b>{moduleLabel}</b> akan di-set ke <b className="text-emerald-700">Izinkan</b>.</div>
                    <div className="text-[10px] px-2 py-1.5 bg-amber-50 border border-amber-300 text-amber-900">
                      ⚠ Permission sensitif (self-approve / edit_all / reopen / delete / confirm_transfer) TIDAK ikut. Anda harus mengaktifkannya manual satu-per-satu via popover tri-state (dgn alasan wajib).
                    </div>
                    <div className="text-[10px] text-slate-500 italic">Visibility scope TIDAK diubah.</div>
                  </>
                )}
                {isReset && (
                  <>
                    <div>Semua override / cell di modul <b>{moduleLabel}</b> akan dihapus (kembali ke <b>Ikuti Default</b>).</div>
                    <div className="text-[10px] text-slate-500 italic">Hanya berlaku untuk modul ini. Modul lain, matrix Divisi &amp; Level, dan data transaksi TIDAK diubah.</div>
                  </>
                )}
              </div>
              <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setModuleActionConfirm(null)}
                  className="px-3 py-1.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]"
                  data-testid="module-action-cancel"
                >Batal</button>
                <button
                  type="button"
                  onClick={() => {
                    if (isAllow) {
                      const { changed, sensitiveSkipped } = applyAllForModule(moduleKey, true);
                      toast.success(`${changed} action di-set ke Izinkan untuk ${moduleLabel}${sensitiveSkipped ? ` · ${sensitiveSkipped} permission sensitif dilewati` : ""}`);
                    } else if (isReset) {
                      const { removed } = resetModuleForActive(moduleKey);
                      toast.info(`${removed} cell direset ke Ikuti Default untuk ${moduleLabel}`);
                    }
                    setModuleActionConfirm(null);
                  }}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] text-white ${isAllow ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 hover:bg-slate-800"}`}
                  data-testid="module-action-confirm"
                >
                  {isAllow ? `Ya, Izinkan ${moduleLabel}` : `Ya, Reset ${moduleLabel}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* R3.2C: Konfirmasi navigasi saat masih dirty */}
      {dirtyNavWarn && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="dirty-nav-warn-dialog" role="dialog" aria-modal="true" aria-labelledby="dirty-nav-title">
          <div className="bg-white border border-slate-300 max-w-md w-full">
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
              <div id="dirty-nav-title" className="font-bold text-amber-900 uppercase text-sm"><span aria-hidden="true">⚠ </span>Perubahan Belum Disimpan</div>
              <div className="text-[10px] text-amber-800 mt-1">Anda memiliki <b>{dirtyKeys.size} perubahan</b> yang belum disimpan. Berpindah target akan membuang perubahan ini.</div>
            </div>
            <div className="p-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDirtyNavWarn(null)}
                className="px-3 py-1.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]"
                data-testid="dirty-nav-cancel"
              >Batal, tetap di sini</button>
              <button
                type="button"
                onClick={() => { resetChanges(); dirtyNavWarn.onProceed?.(); }}
                className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-[10px] uppercase tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]"
                data-testid="dirty-nav-discard"
              >Buang Perubahan &amp; Lanjut</button>
            </div>
          </div>
        </div>
      )}
      {depsDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="deps-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="deps-dialog-title">
          <div className="bg-white border border-slate-300 max-w-md w-full">
            <div className="px-4 py-3 bg-sky-50 border-b border-sky-200">
              <div id="deps-dialog-title" className="font-bold text-sky-900 uppercase text-sm"><span aria-hidden="true">ℹ </span>Aktifkan Permission Dependensi</div>
              <div className="text-[10px] text-sky-800 mt-1">Untuk mengaktifkan <b className="font-mono">{depsDialog.feature}.{depsDialog.key}</b>, permission berikut juga harus aktif efektif. Konfirmasi untuk mengaktifkan sekaligus (tidak akan diaktifkan diam-diam).</div>
            </div>
            <div className="p-4">
              <ul className="text-xs text-slate-700 space-y-1 mb-3 border border-slate-200 p-2 bg-slate-50">
                {depsDialog.requires.map((req) => (
                  <li key={req} className="flex items-center gap-2" data-testid={`deps-req-${req}`}>
                    <span aria-hidden="true" className="text-emerald-600">+</span>
                    <span className="font-mono text-[11px]">{depsDialog.feature}.{req}</span>
                    <span className="text-[10px] text-slate-500 italic">→ akan di-set ke Izinkan</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setDepsDialog(null)} className="px-3 py-1.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]" data-testid="deps-cancel">Batal</button>
                <button onClick={depsDialog.onConfirm} className="px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-[10px] uppercase tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]" data-testid="deps-confirm">Ya, Aktifkan Semua</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


// ============================================================================
// Akses Client & Project (2026-02-11) — 2-tier access matrix panel.
// Tier-1: grant a (division × level) access to a Client → all its projects
// auto-accessible. Tier-2: admin uncheck specific projects to create override.
// New projects created under a granted client → auto-accessible per tier-1.
// BOD Lv1 only.
// ============================================================================
function ClientProjectAccessPanel({ divisions }) {
  const [clients, setClients] = useState([]);          // [{client_name, project_count, projects:[{id,name,type,status,parent_project_id}]}]
  const [grants, setGrants] = useState([]);             // [{client_name, division, level, user_id?, granted}]
  const [overrides, setOverrides] = useState([]);       // [{project_id, division, level, user_id?, granted, client_name}]
  const [staffList, setStaffList] = useState([]);       // [{id, name, division, sub_division, level}]
  const [activeClient, setActiveClient] = useState(""); // client_name
  const [activeDiv, setActiveDiv] = useState("");
  const [activeLevel, setActiveLevel] = useState(1);
  const [targetMode, setTargetMode] = useState("division"); // "division" | "staff" | "project" (2026-09-25: mode Per Project)
  const [activeUserId, setActiveUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const LV_LIST = [1, 2, 3, 4, 5];
  const allDivisions = useMemo(() => {
    const divs = (divisions || []).map((d) => d.name);
    divs.sort((a, b) => (DIVISION_ORDER[a] ?? 99) - (DIVISION_ORDER[b] ?? 99) || a.localeCompare(b));
    return divs;
  }, [divisions]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client-access");
      setClients(data.clients || []);
      setGrants(data.grants || []);
      setOverrides(data.overrides || []);
      const users = (data.users || []).map((u) => ({
        id: u.id, name: u.name, division: u.division, sub_division: u.sub_division, level: u.level,
      }));
      setStaffList(users);
      setActiveUserId((cur) => (cur || (users[0]?.id ?? "")));
      setActiveClient((cur) => (cur || ((data.clients || [])[0]?.client_name ?? "")));
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!activeDiv && allDivisions.length) setActiveDiv(allDivisions[0]); }, [allDivisions, activeDiv]);

  const isStaffMode = targetMode === "staff";

  // Is the current target (div+level OR user_id) currently granted for client_name?
  const isClientGranted = useCallback((client_name) => {
    if (isStaffMode) {
      return grants.some((g) => g.client_name === client_name && g.user_id === activeUserId && g.granted);
    }
    return grants.some((g) => g.client_name === client_name && !g.user_id && g.division === activeDiv && g.level === activeLevel && g.granted);
  }, [grants, activeDiv, activeLevel, activeUserId, isStaffMode]);

  // Find explicit project override for the current target (staff-specific has priority)
  const findOverride = useCallback((project_id) => {
    if (isStaffMode) {
      return overrides.find((o) => o.project_id === project_id && o.user_id === activeUserId);
    }
    return overrides.find((o) => o.project_id === project_id && !o.user_id && o.division === activeDiv && o.level === activeLevel);
  }, [overrides, activeDiv, activeLevel, activeUserId, isStaffMode]);

  // Effective access for a specific project — override > client-level grant
  const isProjectEffectiveGranted = useCallback((project) => {
    const ov = findOverride(project.id);
    if (ov) return !!ov.granted;
    return isClientGranted(activeClient);
  }, [findOverride, activeClient, isClientGranted]);

  // 2026-06-21 (kasus Iro Ganda/RKI): status EFEKTIF per project untuk staff
  // terpilih (mode Override User) — mencakup warisan grant grup divisi×level,
  // penugasan lama (assigned_project_ids), matrix view_all, dll, beserta
  // sumbernya. Tanpa ini, user yang mewarisi akses grup tampak "tidak diberi
  // akses" padahal project-nya tetap terlihat di Manajemen Project.
  const [effectiveMap, setEffectiveMap] = useState({}); // project_id → {status, source, can_revoke}
  const loadEffective = useCallback(async () => {
    if (targetMode !== "staff" || !activeUserId) { setEffectiveMap({}); return; }
    try {
      const { data } = await api.get("/client-access/user-effective-projects", { params: { user_id: activeUserId } });
      const m = {};
      for (const r of data.projects || []) m[r.project_id] = r;
      setEffectiveMap(m);
    } catch { setEffectiveMap({}); }
  }, [targetMode, activeUserId]);
  useEffect(() => { loadEffective(); }, [loadEffective]);

  const EFF_SOURCE_LABELS = {
    client_access_group: "Default: Grup Divisi",
    client_access_user: "Grant Per User",
    project_grant: "Penugasan Lama",
    matrix_view_all: "Matrix: Semua Proyek",
    inherit_child: "Warisan WO/VO",
    inherit_parent: "Warisan Induk",
    internal: "Internal",
    div_level: "Override Divisi",
    super_admin: "Super Admin",
  };

  const toggleClient = async (client_name) => {
    if (isStaffMode) {
      if (!activeUserId) { toast.error("Pilih staff dulu"); return; }
    } else {
      if (!activeDiv) { toast.error("Pilih divisi dulu"); return; }
    }
    const currentlyGranted = isClientGranted(client_name);
    setSaving(true);
    try {
      const payload = isStaffMode
        ? { client_name, user_id: activeUserId, granted: !currentlyGranted }
        : { client_name, division: activeDiv, level: activeLevel, granted: !currentlyGranted };
      await api.patch("/client-access", payload);
      const targetLabel = isStaffMode
        ? (staffList.find((u) => u.id === activeUserId)?.name || "staff")
        : `${activeDiv} Lv${activeLevel}`;
      toast.success(`Client ${client_name} · ${targetLabel}: ${!currentlyGranted ? "diberi akses" : "dicabut"}`);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleProject = async (project) => {
    if (isStaffMode) {
      if (!activeUserId) { toast.error("Pilih staff dulu"); return; }
    } else {
      if (!activeDiv) { toast.error("Pilih divisi dulu"); return; }
    }
    // Mode Override User: baseline = status EFEKTIF (bukan hanya grant per-user),
    // supaya "cabut" terhadap akses warisan grup menghasilkan User Override
    // 'ditolak' yang benar-benar tersimpan (baseline grup dihitung di backend).
    const effRow = isStaffMode ? effectiveMap[project.id] : null;
    const currentlyGranted = effRow ? effRow.status === "allowed" : isProjectEffectiveGranted(project);
    setSaving(true);
    try {
      const payload = isStaffMode
        ? { project_id: project.id, user_id: activeUserId, granted: !currentlyGranted }
        : { project_id: project.id, division: activeDiv, level: activeLevel, granted: !currentlyGranted };
      await api.patch("/project-access-overrides", payload);
      await load();
      await loadEffective();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const activeClientObj = clients.find((c) => c.client_name === activeClient);
  const filteredClients = search
    ? clients.filter((c) => c.client_name.toLowerCase().includes(search.toLowerCase()))
    : clients;
  const activeStaff = staffList.find((u) => u.id === activeUserId);
  const isBod1 = isStaffMode
    ? (activeStaff?.division === "BOD" && Number(activeStaff?.level) === 1)
    : (activeDiv === "BOD" && activeLevel === 1);

  if (loading) return <div className="text-xs text-slate-500 py-8 text-center">Memuat…</div>;
  if (clients.length === 0) {
    return (
      <section className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="client-access-empty">
        Belum ada client project yang dibuat. Buat project (dengan field <b>client</b>) di menu Manajemen Proyek terlebih dahulu.
      </section>
    );
  }

  return (
    <section className="bg-white border border-slate-200" data-testid="client-access-panel">
      <div className="p-4 border-b border-slate-200 bg-slate-50 text-xs text-slate-600">
        Atur akses per <b>Client → Project</b> untuk kombinasi (divisi × level) atau <b>staff spesifik</b>. Klik centang di Client untuk grant semua project di bawahnya (default). Untuk kecualikan project tertentu, hilangkan centangnya di panel kanan.
        <span className="ml-1 font-semibold">Super Admin selalu akses semua</span> (permission `access_control.update`).
      </div>

      {/* Target Mode toggle */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-slate-200 bg-white" data-testid="ca-target-mode">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Target Akses:</span>
        <button
          type="button"
          data-testid="ca-mode-division"
          onClick={() => setTargetMode("division")}
          aria-pressed={targetMode === "division"}
          className={"px-3 py-1 text-[10px] uppercase tracking-wider font-semibold border focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] " + (targetMode === "division" ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400")}
        >Divisi &amp; Level</button>
        <button
          type="button"
          data-testid="ca-mode-staff"
          onClick={() => setTargetMode("staff")}
          aria-pressed={targetMode === "staff"}
          className={"px-3 py-1 text-[10px] uppercase tracking-wider font-semibold border focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] " + (targetMode === "staff" ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400")}
        >Override User</button>
        <button
          type="button"
          data-testid="ca-mode-project"
          onClick={() => setTargetMode("project")}
          aria-pressed={targetMode === "project"}
          className={"px-3 py-1 text-[10px] uppercase tracking-wider font-semibold border focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] " + (targetMode === "project" ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400")}
        >Per Project</button>
        <span className="text-[10px] text-slate-500 ml-2 italic">
          {targetMode === "project"
            ? "Lihat siapa saja yang punya akses ke satu project induk (WO/VO tidak dihitung sebagai project induk)."
            : isStaffMode ? "Grant akses ke pegawai tertentu saja (di luar rule divisi & level)." : "Grant akses ke seluruh pegawai di kombinasi divisi & level."}
        </span>
      </div>

      {targetMode === "project" ? (
        <ProjectAccessView clients={clients} />
      ) : isStaffMode ? (
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 flex-wrap" data-testid="ca-staff-selector">
          <UsersIcon size={13} className="text-slate-500" />
          <SearchableSelect
            value={activeUserId}
            onChange={setActiveUserId}
            options={staffList.map((u) => ({ value: u.id, label: `${u.name} — ${u.division} · Lv${u.level}` }))}
            placeholder="Pilih staff…"
            searchPlaceholder="Cari nama / divisi…"
            emptyText="Tidak ada hasil"
            testid="ca-staff-dropdown"
          />
          {activeStaff && (
            <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[activeStaff.division] || "bg-slate-600 text-white"}`}>{activeStaff.division} Lv{activeStaff.level}</span>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap border-b border-slate-200 bg-white" data-testid="ca-div-tabs">
            {allDivisions.map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`ca-div-tab-${d}`}
                onClick={() => setActiveDiv(d)}
                className={"px-4 py-2 text-[11px] uppercase tracking-wider font-semibold border-r border-slate-200 " + (activeDiv === d ? "bg-[#0B1D3A] text-white" : "text-slate-600 hover:bg-slate-50")}
              >{d}</button>
            ))}
          </div>
          <div className="flex border-b border-slate-200 bg-slate-50" data-testid="ca-lv-tabs">
            {LV_LIST.map((lv) => (
              <button
                key={lv}
                type="button"
                data-testid={`ca-lv-tab-${lv}`}
                onClick={() => setActiveLevel(lv)}
                className={"px-5 py-2 text-[11px] uppercase tracking-wider font-semibold border-r border-slate-200 " + (activeLevel === lv ? "bg-[#FF5A1F] text-white" : "text-slate-700 hover:bg-white")}
              >LV{lv}</button>
            ))}
          </div>
        </>
      )}

      {targetMode !== "project" && isBod1 && (
        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 text-[11px] text-emerald-800">
          <b>BOD Lv1</b> super-admin — akses ke seluruh client & project otomatis aktif dan tidak bisa dibatasi dari sini.
        </div>
      )}

      {targetMode !== "project" && (
      <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-slate-200">
        {/* Left: Client list */}
        <div className="min-h-[300px] flex flex-col">
          <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
            <Building2 size={14} className="text-slate-500" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">Daftar Client</span>
            <span className="text-[10px] tabular-nums text-slate-500 ml-1">({clients.length})</span>
            <div className="ml-auto">
              <Input
                data-testid="ca-search-client"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari client…"
                className="rounded-none h-7 text-[11px] w-40"
              />
            </div>
          </div>
          <ul className="flex-1 overflow-y-auto max-h-[500px] divide-y divide-slate-100">
            {filteredClients.map((c) => {
              const granted = isBod1 ? true : isClientGranted(c.client_name);
              const isActive = activeClient === c.client_name;
              // Count effective grants under this client for the ACTIVE target (div/level OR user)
              const projCount = c.projects.length;
              const effectiveGranted = c.projects.filter((p) => {
                const ov = findOverride(p.id);
                if (ov) return !!ov.granted;
                return granted;
              }).length;
              return (
                <li
                  key={c.client_name}
                  data-testid={`ca-client-row-${c.client_name}`}
                  onClick={() => setActiveClient(c.client_name)}
                  className={"flex items-center gap-2 px-3 py-2 cursor-pointer " + (isActive ? "bg-orange-50 border-l-4 border-[#FF5A1F]" : "hover:bg-slate-50 border-l-4 border-transparent")}
                >
                  <button
                    type="button"
                    data-testid={`ca-client-toggle-${c.client_name}`}
                    disabled={isBod1 || saving}
                    onClick={(e) => { e.stopPropagation(); if (!isBod1) toggleClient(c.client_name); }}
                    className={"w-5 h-5 border inline-flex items-center justify-center shrink-0 " + (granted ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-slate-300 hover:border-slate-400") + (isBod1 ? " opacity-70 cursor-not-allowed" : "")}
                    title={isBod1 ? "BOD Lv1 super-admin" : (granted ? "Klik untuk cabut akses Client + semua Project di bawahnya" : "Klik untuk grant akses ke Client + semua Project default")}
                  >{granted && <Check size={14} strokeWidth={3} />}</button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0B1D3A] truncate">{c.client_name}</div>
                    <div className="text-[10px] text-slate-500 tabular-nums">
                      {projCount} project · <span className={effectiveGranted === projCount ? "text-emerald-600" : effectiveGranted === 0 ? "text-rose-600" : "text-amber-600"}>{effectiveGranted} accessible</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right: Projects under the selected client */}
        <div className="min-h-[300px] flex flex-col">
          <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
            <FolderKanban size={14} className="text-slate-500" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 truncate">
              Project · {activeClient || <span className="italic text-slate-400">Pilih Client</span>}
            </span>
            {activeClientObj && (
              <span className="ml-auto text-[10px] tabular-nums text-slate-500">({activeClientObj.projects.length})</span>
            )}
          </div>
          <ul className="flex-1 overflow-y-auto max-h-[500px] divide-y divide-slate-100" data-testid="ca-project-list">
            {!activeClientObj && (
              <li className="p-8 text-center text-xs text-slate-400 italic">Pilih client di kiri untuk lihat daftar project</li>
            )}
            {activeClientObj?.projects.map((p) => {
              const clientGranted = isClientGranted(activeClient);
              const ov = findOverride(p.id);
              const eff = isStaffMode ? effectiveMap[p.id] : null;
              const effectiveGranted = isBod1 ? true : (eff ? eff.status === "allowed" : (ov ? !!ov.granted : clientGranted));
              const hasOverride = !!ov;
              const effSource = (isStaffMode && eff && eff.status === "allowed" && eff.source !== "user_override") ? eff.source : null;
              return (
                <li key={p.id} data-testid={`ca-project-row-${p.id}`} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                  <button
                    type="button"
                    data-testid={`ca-project-toggle-${p.id}`}
                    disabled={isBod1 || saving || (!isStaffMode && !clientGranted && !hasOverride)}
                    onClick={() => toggleProject(p)}
                    className={"w-5 h-5 border inline-flex items-center justify-center shrink-0 " + (effectiveGranted ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-slate-300 hover:border-slate-400") + ((isBod1 || (!isStaffMode && !clientGranted && !hasOverride)) ? " opacity-40 cursor-not-allowed" : "")}
                    title={isBod1 ? "BOD Lv1 super-admin" : (isStaffMode ? (effectiveGranted ? "Klik untuk cabut akses user ini dari project ini (membuat User Override 'ditolak')" : "Klik untuk grant khusus user ini ke project ini") : (!clientGranted && !hasOverride ? "Grant Client dulu untuk mengelola project ini" : (effectiveGranted ? "Klik untuk kecualikan project ini" : "Klik untuk grant khusus project ini")))}
                  >{effectiveGranted && <Check size={14} strokeWidth={3} />}</button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#0B1D3A] truncate flex items-center gap-1.5">
                      {p.name}
                      {p.type === "variation_order" && <span className="px-1 py-0 text-[8px] font-semibold uppercase tracking-wider bg-[#FF5A1F] text-white">VO</span>}
                      {p.type === "working_order" && <span className="px-1 py-0 text-[8px] font-semibold uppercase tracking-wider bg-purple-700 text-white">WO</span>}
                      {hasOverride && <span className="px-1 py-0 text-[8px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300" title="Override manual — beda dari default client">Override</span>}
                      {effSource && <span className="px-1 py-0 text-[8px] font-semibold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-300" data-testid={`ca-eff-src-${p.id}`} title="Sumber akses efektif user ini (bukan override per-user)">{EFF_SOURCE_LABELS[effSource] || effSource}</span>}
                    </div>
                    <div className="text-[10px] text-slate-500 tabular-nums">Status: {p.status || "aktif"}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      )}
    </section>
  );
}

// ============================================================================
// Client Access Audit Log Panel (2026-02-11) — BOD Lv1 hanya. Menampilkan siapa
// grant/revoke apa kapan, dengan filter divisi, level, action type, tanggal,
// dan client search. Read-only.
// ============================================================================
function ClientAccessAuditLogPanel({ divisions }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    division: "all", level: "all", action: "all",
    date_from: "", date_to: "", client: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.division !== "all") params.set("division", filters.division);
      if (filters.level !== "all") params.set("level", filters.level);
      if (filters.action !== "all") params.set("action", filters.action);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.client) params.set("client", filters.client);
      params.set("limit", "500");
      const { data } = await api.get(`/client-access/audit-log?${params.toString()}`);
      setRows(data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const ACTION_META = {
    grant: { label: "Grant Client", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    revoke: { label: "Revoke Client", cls: "bg-rose-100 text-rose-800 border-rose-300" },
    project_override_grant: { label: "Override Grant", cls: "bg-blue-100 text-blue-800 border-blue-300" },
    project_override_revoke: { label: "Override Revoke", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  };

  const allDivisions = useMemo(() => {
    const divs = (divisions || []).map((d) => d.name);
    divs.sort((a, b) => (DIVISION_ORDER[a] ?? 99) - (DIVISION_ORDER[b] ?? 99) || a.localeCompare(b));
    return divs;
  }, [divisions]);

  const resetFilters = () => setFilters({
    division: "all", level: "all", action: "all",
    date_from: "", date_to: "", client: "",
  });

  return (
    <section className="bg-white border border-slate-200" data-testid="access-audit-panel">
      <header className="p-4 border-b border-slate-200 bg-slate-50 flex items-start justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-600">
          <div className="font-semibold uppercase tracking-wider text-[#0B1D3A] mb-0.5 flex items-center gap-1.5"><FileClock size={13} /> Audit Log Akses Client &amp; Project</div>
          <div>Semua aktivitas grant / revoke akses Client &amp; override Project tercatat di sini. Hanya BOD Lv1 yang dapat melihat.</div>
        </div>
        <Button
          type="button"
          data-testid="btn-audit-reset-filters"
          variant="outline"
          onClick={resetFilters}
          className="rounded-none h-8 text-[10px] uppercase tracking-wider flex items-center gap-1"
        ><RotateCcw size={11} /> Reset Filter</Button>
      </header>

      {/* Filter bar */}
      <div className="p-3 border-b border-slate-200 bg-white grid grid-cols-2 md:grid-cols-6 gap-2 text-xs" data-testid="audit-filter-bar">
        <div>
          <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block mb-0.5">Divisi</label>
          <Select value={filters.division} onValueChange={(v) => setFilters((f) => ({ ...f, division: v }))}>
            <SelectTrigger data-testid="audit-filter-division" className="rounded-none h-7 text-[11px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Divisi</SelectItem>
              {allDivisions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block mb-0.5">Level</label>
          <Select value={filters.level} onValueChange={(v) => setFilters((f) => ({ ...f, level: v }))}>
            <SelectTrigger data-testid="audit-filter-level" className="rounded-none h-7 text-[11px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Level</SelectItem>
              {[1, 2, 3, 4, 5].map((lv) => <SelectItem key={lv} value={String(lv)}>LV{lv}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block mb-0.5">Aksi</label>
          <Select value={filters.action} onValueChange={(v) => setFilters((f) => ({ ...f, action: v }))}>
            <SelectTrigger data-testid="audit-filter-action" className="rounded-none h-7 text-[11px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Aksi</SelectItem>
              <SelectItem value="grant">Grant Client</SelectItem>
              <SelectItem value="revoke">Revoke Client</SelectItem>
              <SelectItem value="project_override_grant">Override Grant</SelectItem>
              <SelectItem value="project_override_revoke">Override Revoke</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block mb-0.5">Dari Tanggal</label>
          <Input type="date" data-testid="audit-filter-date-from" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="rounded-none h-7 text-[11px]" />
        </div>
        <div>
          <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block mb-0.5">Sampai Tanggal</label>
          <Input type="date" data-testid="audit-filter-date-to" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="rounded-none h-7 text-[11px]" />
        </div>
        <div>
          <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block mb-0.5">Cari Client</label>
          <Input
            data-testid="audit-filter-client"
            value={filters.client}
            onChange={(e) => setFilters((f) => ({ ...f, client: e.target.value }))}
            placeholder="Substring nama…"
            className="rounded-none h-7 text-[11px]"
          />
        </div>
      </div>

      {/* 149-ch: Access Matrix table muat pada desktop 1280 — TIDAK PERLU
          overflow-x-auto wrapper. Wrapper `overflow-y-auto max-h-[600px]`
          sebelumnya membuat thead sticky terjebak dalam scroll ancestor
          sehingga header hilang saat scroll page. Buang keduanya. */}
      <div className="min-w-0 w-full">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 uppercase tracking-wider text-[10px] text-slate-600 ens-sticky-thead">
            <tr>
              <th className="text-left px-3 py-2 w-36">Waktu</th>
              <th className="text-left px-3 py-2 w-32">Aksi</th>
              <th className="text-left px-3 py-2">Client</th>
              <th className="text-left px-3 py-2 w-28">Target</th>
              <th className="text-right px-3 py-2 w-16">Project</th>
              <th className="text-left px-3 py-2 w-40">Actor</th>
            </tr>
          </thead>
          <tbody data-testid="audit-log-table">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400 italic">Memuat…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400 italic">Tidak ada catatan audit yang cocok dengan filter</td></tr>
            ) : rows.map((r, i) => {
              const meta = ACTION_META[r.action] || { label: r.action, cls: "bg-slate-100 text-slate-800 border-slate-300" };
              const dt = r.created_at ? new Date(r.created_at) : null;
              const affected = (r.affected_project_ids || []).length;
              return (
                <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${i}`}>
                  <td className="px-3 py-2 tabular-nums text-slate-700">
                    {dt ? dt.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 border text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>
                  </td>
                  <td className="px-3 py-2 font-semibold text-[#0B1D3A]">
                    {r.client_name || <span className="text-slate-300 italic">—</span>}
                    {r.project_name && <div className="text-[10px] text-slate-500 font-normal">Project: {r.project_name}</div>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.target_user_id ? (
                      <>
                        <span className="px-1.5 py-0.5 text-[10px] bg-indigo-100 text-indigo-800 border border-indigo-300 font-semibold uppercase tracking-wider">Staff</span>
                        <div className="text-[10px] text-slate-700 mt-0.5">{r.target_user_name || r.target_user_id}</div>
                      </>
                    ) : (
                      <>
                        <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[r.target_division] || "bg-slate-600 text-white"}`}>{r.target_division}</span>
                        <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-slate-100 font-semibold uppercase tracking-wider">Lv{r.target_level}</span>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{affected}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {r.actor_name}
                    <div className="text-[10px] text-slate-400">{r.actor_division}·Lv{r.actor_level}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className="p-2 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 flex items-center justify-between">
        <span data-testid="audit-count">{loading ? "…" : `${rows.length} catatan`}</span>
        <span className="italic">Klik Reset Filter untuk lihat semuanya</span>
      </footer>
    </section>
  );
}

